import { Component, useState, useEffect, useCallback, useRef } from "react";

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#0d1117", color: "#e6edf3" }}>
          <div style={{ textAlign: "center", padding: 32 }}>
            <p style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Something went wrong</p>
            <p style={{ fontSize: 13, color: "#8b949e", marginBottom: 24 }}>Please refresh the page. If the problem persists, contact support.</p>
            <button onClick={() => window.location.reload()} style={{ background: "#c47a10", border: "none", borderRadius: 8, color: "#fff", cursor: "pointer", fontSize: 14, fontWeight: 600, padding: "12px 24px" }}>
              Refresh page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── Config ────────────────────────────────────────────────────────────────────
// These values come from your .env.local file in the Next.js backend
const API_BASE = "https://portal.powervisionbi.com/api";
const ENTRA_CLIENT_ID = "db805003-64da-4cc7-80eb-8e852d8e651f";
const ENTRA_AUTHORITY = "https://login.microsoftonline.com/common";
const ENTRA_REDIRECT_URI = "https://portal.powervisionbi.com";
// ─── MSAL (Microsoft Authentication Library) ──────────────────────────────────
// Load MSAL from CDN — add this to your index.html:
// <script src="https://alcdn.msauth.net/browser/2.38.0/js/msal-browser.min.js"></script>
let _msalInstance = null;
function getMsalInstance() {
  if (!window.msal) return null;
  if (_msalInstance) return _msalInstance;
  _msalInstance = new window.msal.PublicClientApplication({
    auth: {
      clientId: ENTRA_CLIENT_ID,
      authority: ENTRA_AUTHORITY,
      redirectUri: ENTRA_REDIRECT_URI,
    },
    cache: { cacheLocation: "sessionStorage" },
  });
  return _msalInstance;
}

// ─── Auth helpers ──────────────────────────────────────────────────────────────
async function signInWithEntra() {
  const msal = getMsalInstance();
  if (!msal) throw new Error("MSAL not loaded");
  await msal.initialize();
  await msal.loginRedirect({
    scopes: ["openid", "profile", "email"],
  });
}

async function getAccessToken() {
  const msal = getMsalInstance();
  if (!msal) return null;
  await msal.initialize();
  const accounts = msal.getAllAccounts();
  if (!accounts.length) return null;
  try {
    const result = await msal.acquireTokenSilent({
      scopes: ["openid", "profile", "email"],
      account: accounts[0],
    });
    return result.accessToken;
  } catch {
    return null;
  }
}

async function signOut() {
  const msal = getMsalInstance();
  if (!msal) return;
  await msal.initialize();
  await msal.logoutPopup();
}

// ─── API calls ─────────────────────────────────────────────────────────────────
function downloadExcel(data, question) {
  if (!data?.length) return;
  const headers = Object.keys(data[0]);
  const escape = v => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = '﻿' + [headers.join(','), ...data.map(row => headers.map(h => escape(row[h])).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(question || 'export').slice(0, 40).replace(/[^a-z0-9]/gi, '_')}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function extractKpiValue(answer) {
  if (!answer) return null;
  // Strip year-like numbers (1900-2199) so they don't shadow business values
  const cleaned = answer.replace(/\b(19|20|21)\d{2}\b/g, '');
  const m =
    cleaned.match(/[R$£€]\s*[\d,]+(\.\d+)?\s*[KMBkmb]?\b/) ||
    cleaned.match(/\b[\d,]+(\.\d+)?\s*[KMBkmb]\b/) ||
    cleaned.match(/\b[\d]{1,3}(,\d{3})+(\.\d+)?\b/) ||
    cleaned.match(/\b\d+\.\d+%?\b/) ||
    cleaned.match(/\b\d+%\b/) ||
    cleaned.match(/\b\d{4,}\b/) ||
    cleaned.match(/\b\d{1,3}\b/);
  return m ? m[0].trim() : null;
}

function authHeaders(token, impersonateTenantId) {
  const t = token || sessionStorage.getItem("pvbi_token");
  const h = { Authorization: `Bearer ${t}` };
  if (impersonateTenantId) h["X-Impersonate-Tenant"] = impersonateTenantId;
  return h;
}

async function fetchReports(token, impersonateTenantId) {
  const res = await fetch(`${API_BASE}/reports`, {
    headers: authHeaders(token, impersonateTenantId),
  });
  if (!res.ok) throw new Error("Failed to load reports");
  return res.json();
}

async function fetchEmbedToken(token, reportId, impersonateTenantId) {
  const res = await fetch(`${API_BASE}/embed-token`, {
    method: "POST",
    headers: { ...authHeaders(token, impersonateTenantId), "Content-Type": "application/json" },
    body: JSON.stringify({ reportId }),
  });
  if (!res.ok) throw new Error("Failed to get embed token");
  return res.json();
}

// ─── Theme ─────────────────────────────────────────────────────────────────────
const T = {
  bg: "#0d1117", surface: "#161b22", surfaceHover: "#1f2937",
  border: "#21262d", borderHover: "#30363d",
  accent: "#c47a10", accentDark: "#a86209", accentLight: "#e8950f",
  accentGlow: "rgba(196,122,16,0.15)", accentGlow2: "rgba(196,122,16,0.07)",
  success: "#2ea043", successGlow: "rgba(46,160,67,0.15)",
  error: "#f85149", errorGlow: "rgba(248,81,73,0.12)",
  warn: "#d29922", textPrimary: "#e6edf3",
  textSecondary: "#8b949e", textMuted: "#484f58",
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Plus Jakarta Sans',sans-serif;background:${T.bg};color:${T.textPrimary};min-height:100vh;overflow-x:hidden}
  .mono{font-family:'JetBrains Mono',monospace}
  .grid-bg{position:fixed;inset:0;pointer-events:none;z-index:0;
    background-image:linear-gradient(rgba(196,122,16,0.035) 1px,transparent 1px),linear-gradient(90deg,rgba(196,122,16,0.035) 1px,transparent 1px);
    background-size:48px 48px}
  .orb{position:fixed;border-radius:50%;pointer-events:none;z-index:0;filter:blur(90px)}
  @keyframes fadeUp{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}
  @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
  @keyframes pulse2{0%,100%{opacity:1}50%{opacity:.45}}
  .fu{animation:fadeUp .45s ease both}
  .fu1{animation:fadeUp .45s .06s ease both}
  .fu2{animation:fadeUp .45s .13s ease both}
  .fu3{animation:fadeUp .45s .2s ease both}
  .spin{animation:spin .7s linear infinite}
  .field-group{display:flex;flex-direction:column;gap:6px}
  .field-label{font-size:11px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:${T.textSecondary}}
  .field-input{background:rgba(255,255,255,.04);border:1px solid ${T.border};border-radius:8px;color:${T.textPrimary};font-family:'Plus Jakarta Sans',sans-serif;font-size:14px;padding:11px 14px;outline:none;transition:border-color .2s,box-shadow .2s,background .2s;width:100%}
  .field-input:focus{border-color:${T.accent};box-shadow:0 0 0 3px ${T.accentGlow};background:rgba(196,122,16,.04)}
  .field-input::placeholder{color:${T.textMuted}}
  .btn-primary{background:${T.accent};border:none;border-radius:8px;color:#fff;cursor:pointer;font-family:'Plus Jakarta Sans',sans-serif;font-size:14px;font-weight:600;padding:12px 24px;transition:background .2s,transform .1s,box-shadow .2s;width:100%}
  .btn-primary:hover{background:${T.accentLight};box-shadow:0 0 28px ${T.accentGlow};transform:translateY(-1px)}
  .btn-primary:active{transform:translateY(0)}
  .btn-primary:disabled{opacity:.45;cursor:not-allowed;transform:none}
  .btn-secondary{background:transparent;border:1px solid ${T.border};border-radius:8px;color:${T.textSecondary};cursor:pointer;font-family:'Plus Jakarta Sans',sans-serif;font-size:13px;font-weight:500;padding:9px 18px;transition:border-color .2s,color .2s,background .2s}
  .btn-secondary:hover{border-color:${T.borderHover};color:${T.textPrimary};background:rgba(255,255,255,.04)}
  .btn-ghost{background:none;border:none;color:${T.accent};cursor:pointer;font-family:'Plus Jakarta Sans',sans-serif;font-size:13px;font-weight:500;padding:4px 0;transition:opacity .2s}
  .btn-ghost:hover{opacity:.72}
  .card{background:${T.surface};border:1px solid ${T.border};border-radius:12px;padding:24px}
  .divider{border:none;border-top:1px solid ${T.border};margin:20px 0}
  .spinner{width:17px;height:17px;border:2px solid rgba(255,255,255,.2);border-top-color:#fff;border-radius:50%;display:inline-block}
  .alert{border-radius:8px;padding:12px 16px;font-size:13px;font-weight:500;display:flex;align-items:flex-start;gap:10px;animation:fadeUp .3s ease}
  .alert-error{background:${T.errorGlow};border:1px solid rgba(248,81,73,.25);color:#fca5a5}
  .alert-success{background:${T.successGlow};border:1px solid rgba(46,160,67,.25);color:#86efac}
  .alert-info{background:${T.accentGlow2};border:1px solid rgba(196,122,16,.2);color:#fcd34d}
  .badge{display:inline-flex;align-items:center;gap:5px;border-radius:20px;font-size:11px;font-weight:600;letter-spacing:.04em;padding:3px 10px}
  .badge-success{background:${T.successGlow};color:${T.success};border:1px solid rgba(46,160,67,.25)}
  .badge-info{background:${T.accentGlow};color:${T.accent};border:1px solid rgba(196,122,16,.25)}
  .nav-item{display:flex;align-items:center;gap:10px;padding:10px 14px;border-radius:8px;cursor:pointer;font-size:13px;font-weight:500;transition:background .15s,color .15s;color:${T.textSecondary};border:none;background:none;width:100%;text-align:left;font-family:'Plus Jakarta Sans',sans-serif}
  .nav-item:hover{background:rgba(255,255,255,.05);color:${T.textPrimary}}
  .nav-item.active{background:${T.accentGlow};color:${T.accent}}
  .report-card{background:${T.surface};border:1px solid ${T.border};border-radius:10px;padding:20px;cursor:pointer;transition:border-color .2s,transform .2s,box-shadow .2s;animation:fadeUp .4s ease both}
  .report-card:hover{border-color:${T.accent};transform:translateY(-2px);box-shadow:0 8px 32px rgba(196,122,16,.12)}
  .stat-card{background:${T.surface};border:1px solid ${T.border};border-radius:10px;padding:18px 20px;animation:fadeUp .4s ease both}
  ::-webkit-scrollbar{width:5px}
  ::-webkit-scrollbar-track{background:transparent}
  ::-webkit-scrollbar-thumb{background:${T.border};border-radius:3px}
  @media print{
    body > * { display: none !important; }
    #report-print-frame { display: block !important; position: fixed; inset: 0; width: 100vw; height: 100vh; border: none; z-index: 9999; background: white; }
  }
  .hamburger-btn{display:none;align-items:center;justify-content:center;background:none;border:1px solid ${T.border};border-radius:8px;color:${T.textSecondary};cursor:pointer;padding:8px;flex-shrink:0}
  .sidebar-overlay{display:none;position:fixed;inset:0;background:rgba(0,0,0,.55);z-index:199;cursor:pointer}
  @media(max-width:768px){
    .hamburger-btn{display:flex !important}
    .mob-sidebar{position:fixed !important;left:0;top:0;bottom:0;z-index:200;transform:translateX(-100%);transition:transform .3s ease;overflow-y:auto}
    .mob-sidebar.open{transform:translateX(0) !important;box-shadow:4px 0 32px rgba(0,0,0,.6)}
    .sidebar-overlay.open{display:block}
    .kpi-grid{grid-template-columns:1fr !important}
    .reports-grid{grid-template-columns:1fr !important}
    .mob-pad{padding:18px 14px !important}
    .mob-stats{grid-template-columns:1fr !important}
    .mob-header{flex-direction:column;align-items:flex-start !important;gap:12px}
  }
  @media(min-width:769px) and (max-width:1100px){
    .kpi-grid{grid-template-columns:repeat(2,1fr) !important}
    .reports-grid{grid-template-columns:repeat(2,1fr) !important}
  }
`;

const Icon = ({ n, size = 16, color = "currentColor" }) => {
  const icons = {
    eye: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
    db: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>,
    chart: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
    grid: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>,
    settings: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
    logout: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
    check: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
    x: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
    alert: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
    user: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
    lock: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
    refresh: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>,
    external: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>,
    microsoft: <svg width={size} height={size} viewBox="0 0 24 24" fill="none"><rect x="1" y="1" width="10.5" height="10.5" fill="#f25022"/><rect x="12.5" y="1" width="10.5" height="10.5" fill="#7fba00"/><rect x="1" y="12.5" width="10.5" height="10.5" fill="#00a4ef"/><rect x="12.5" y="12.5" width="10.5" height="10.5" fill="#ffb900"/></svg>,
    zap: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
    home: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
    send: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>,
    bell: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
    trash: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>,
    star: <svg width={size} height={size} viewBox="0 0 24 24" fill={color} stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
    starEmpty: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
    messageSquare: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
    clock: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
    shield: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
    menu: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>,
    users: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  };
  return icons[n] || null;
};

const CAT_COLOR = { Finance: T.accent, Sales: T.success, Operations: "#a78bfa", Purchasing: T.warn };

// ─── Chart components ──────────────────────────────────────────────────────────
const CHART_COLORS = ["#c47a10", "#2ea043", "#3b82f6", "#a78bfa", "#f59e0b", "#ef4444", "#06b6d4", "#ec4899"];

function BarChart({ data, title }) {
  if (!data?.length) return null;
  const maxVal = Math.max(...data.map(d => d.value), 1);
  const W = 700, H = 280, PX = 50, PY = 36, GAP = 8;
  const barW = Math.max(8, (W - PX * 2 - GAP * (data.length - 1)) / data.length);
  return (
    <svg viewBox={`0 0 ${W} ${H + 48}`} style={{ width: "100%", display: "block" }}>
      <text x={W / 2} y={16} textAnchor="middle" fill={T.textPrimary} fontSize={12} fontWeight={600}>{title}</text>
      <line x1={PX} y1={PY} x2={PX} y2={H} stroke={T.border} />
      <line x1={PX} y1={H} x2={W - PX} y2={H} stroke={T.border} />
      {data.map((d, i) => {
        const bh = Math.max(2, ((d.value / maxVal) * (H - PY - 8)));
        const x = PX + i * (barW + GAP);
        const y = H - bh;
        const label = d.name.length > 11 ? d.name.slice(0, 10) + "…" : d.name;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={bh} rx={3} fill={CHART_COLORS[i % CHART_COLORS.length]} opacity={0.88} />
            <text x={x + barW / 2} y={y - 4} textAnchor="middle" fill={T.textSecondary} fontSize={9}>
              {d.value >= 1000000 ? `${(d.value / 1000000).toFixed(1)}M` : d.value >= 1000 ? `${(d.value / 1000).toFixed(0)}K` : d.value}
            </text>
            <text x={x + barW / 2} y={H + 14} textAnchor="middle" fill={T.textMuted} fontSize={9}
              transform={data.length > 5 ? `rotate(-30,${x + barW / 2},${H + 14})` : undefined}>{label}</text>
          </g>
        );
      })}
    </svg>
  );
}

function LineChart({ data, title }) {
  if (!data?.length) return null;
  const vals = data.map(d => d.value);
  const maxVal = Math.max(...vals, 1), minVal = Math.min(...vals, 0);
  const range = maxVal - minVal || 1;
  const W = 700, H = 280, PX = 56, PY = 36;
  const px = W - PX * 2, py = H - PY - 12;
  const pts = data.map((d, i) => ({
    x: PX + (data.length === 1 ? px / 2 : (i / (data.length - 1)) * px),
    y: PY + py - ((d.value - minVal) / range) * py,
    ...d,
  }));
  const path = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");
  const area = `${path} L ${pts[pts.length - 1].x} ${H - 12} L ${pts[0].x} ${H - 12} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H + 32}`} style={{ width: "100%", display: "block" }}>
      <text x={W / 2} y={16} textAnchor="middle" fill={T.textPrimary} fontSize={12} fontWeight={600}>{title}</text>
      <line x1={PX} y1={PY} x2={PX} y2={H - 12} stroke={T.border} />
      <line x1={PX} y1={H - 12} x2={W - PX} y2={H - 12} stroke={T.border} />
      <defs>
        <linearGradient id="lineGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={T.accent} stopOpacity="0.25" />
          <stop offset="100%" stopColor={T.accent} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#lineGrad)" />
      <path d={path} fill="none" stroke={T.accent} strokeWidth={2} strokeLinejoin="round" />
      {pts.map((p, i) => {
        const label = p.name.length > 8 ? p.name.slice(0, 7) + "…" : p.name;
        return (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r={4} fill={T.accent} />
            <text x={p.x} y={p.y - 8} textAnchor="middle" fill={T.textSecondary} fontSize={9}>
              {p.value >= 1000000 ? `${(p.value / 1000000).toFixed(1)}M` : p.value >= 1000 ? `${(p.value / 1000).toFixed(0)}K` : p.value}
            </text>
            <text x={p.x} y={H + 6} textAnchor="middle" fill={T.textMuted} fontSize={9}
              transform={data.length > 6 ? `rotate(-30,${p.x},${H + 6})` : undefined}>{label}</text>
          </g>
        );
      })}
    </svg>
  );
}

function PieChart({ data, title }) {
  if (!data?.length) return null;
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  const CX = 140, CY = 150, R = 120, W = 520;
  let angle = -Math.PI / 2;
  const slices = data.map((d, i) => {
    const sweep = (d.value / total) * 2 * Math.PI;
    const x1 = CX + R * Math.cos(angle);
    const y1 = CY + R * Math.sin(angle);
    angle += sweep;
    const x2 = CX + R * Math.cos(angle);
    const y2 = CY + R * Math.sin(angle);
    return { d: `M${CX},${CY} L${x1.toFixed(1)},${y1.toFixed(1)} A${R},${R} 0 ${sweep > Math.PI ? 1 : 0} 1 ${x2.toFixed(1)},${y2.toFixed(1)} Z`, color: CHART_COLORS[i % CHART_COLORS.length], pct: Math.round(d.value / total * 100), name: d.name };
  });
  return (
    <svg viewBox={`0 0 ${W} 310`} style={{ width: "100%", display: "block" }}>
      <text x={W / 2} y={16} textAnchor="middle" fill={T.textPrimary} fontSize={12} fontWeight={600}>{title}</text>
      {slices.map((s, i) => <path key={i} d={s.d} fill={s.color} opacity={0.88} stroke={T.bg} strokeWidth={1} />)}
      {slices.map((s, i) => {
        const label = s.name.length > 16 ? s.name.slice(0, 15) + "…" : s.name;
        return (
          <g key={i} transform={`translate(280,${40 + i * 24})`}>
            <rect width={10} height={10} rx={2} fill={s.color} />
            <text x={14} y={9} fill={T.textSecondary} fontSize={10}>{label} ({s.pct}%)</text>
          </g>
        );
      })}
    </svg>
  );
}

function ChartWidget({ chart }) {
  if (!chart) return null;
  return (
    <div style={{ marginTop: 12, padding: "14px 12px", background: T.bg, borderRadius: 8, border: `1px solid ${T.border}`, overflowX: "auto" }}>
      {chart.type === "bar" && <BarChart data={chart.data} title={chart.title} />}
      {chart.type === "line" && <LineChart data={chart.data} title={chart.title} />}
      {chart.type === "pie" && <PieChart data={chart.data} title={chart.title} />}
    </div>
  );
}

// ─── Login ─────────────────────────────────────────────────────────────────────
// Now uses Microsoft Entra External ID via MSAL popup
function Login({ onLogin }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const handleMicrosoftSignIn = async () => {
    setErr(""); setLoading(true);
    try {
      await signInWithEntra(); // redirects away — no return value
    } catch (e) {
      setErr("Sign-in failed. Please try again or contact Power Vision BI support.");
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, position: "relative", zIndex: 1 }}>
      <div style={{ width: "100%", maxWidth: 400 }}>
        <div className="fu" style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 40 }}>
          <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: "-0.02em", color: T.textPrimary }}>
            Power<span style={{ color: T.accent }}>Vision</span> BI
          </div>
          <div style={{ marginTop: 10, height: 1, width: 200, background: `linear-gradient(90deg, transparent, ${T.accent}, transparent)` }} />
          <p style={{ marginTop: 10, fontSize: 12, color: T.textMuted, letterSpacing: "0.06em" }}>CLIENT ANALYTICS PORTAL</p>
        </div>

        <div className="card fu1" style={{ padding: 32 }}>
          <h2 style={{ fontSize: 19, fontWeight: 600, marginBottom: 4 }}>Welcome back</h2>
          <p style={{ fontSize: 13, color: T.textSecondary, marginBottom: 28 }}>Sign in with your company account to view your reports</p>

          {err && <div className="alert alert-error" style={{ marginBottom: 18 }}><Icon n="alert" size={15} color={T.error} />{err}</div>}

          {/* Microsoft sign-in button */}
          <button
            className="btn-primary"
            onClick={handleMicrosoftSignIn}
            disabled={loading}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, background: "#fff", color: "#1a1a1a", border: "1px solid #ddd" }}
          >
            {loading
              ? <><span className="spinner spin" style={{ borderTopColor: T.accent, borderColor: "rgba(196,122,16,.3)" }} />Signing in…</>
              : <><Icon n="microsoft" size={18} />Continue with Microsoft</>
            }
          </button>

          <hr className="divider" />
          <p style={{ fontSize: 12, color: T.textMuted, textAlign: "center" }}>
            New customer? <button className="btn-ghost" style={{ fontSize: 12 }}>Contact Power Vision BI</button>
          </p>
        </div>

        <p className="fu2" style={{ textAlign: "center", marginTop: 24, fontSize: 11, color: T.textMuted }}>
          <span style={{ color: "#ffffff", fontWeight: 600, letterSpacing: "0.04em" }}>We Make Data Dance!</span> · Secured with Microsoft Entra ID
        </p>
      </div>
    </div>
  );
}

// ─── Dashboard ──────────────────────────────────────────────────────────────────
// Now loads real reports from /api/reports and embeds via /api/embed-token
// ─── Analytics charts (Plotly via CDN window.Plotly) ─────────────────────────
function PlotlyChart({ id, traces, layout }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || !window.Plotly) return;
    const baseLayout = {
      paper_bgcolor: T.surface, plot_bgcolor: T.surface,
      font: { color: T.textPrimary, family: "'Plus Jakarta Sans',sans-serif", size: 11 },
      margin: { t: 10, r: 10, b: 60, l: 60 },
      xaxis: { gridcolor: T.border, linecolor: T.border, tickcolor: T.border },
      yaxis: { gridcolor: T.border, linecolor: T.border, tickcolor: T.border },
      legend: { bgcolor: 'rgba(0,0,0,0)', font: { color: T.textSecondary } },
      ...layout,
    };
    window.Plotly.newPlot(ref.current, traces, baseLayout, { responsive: true, displayModeBar: false });
    return () => { if (ref.current) window.Plotly.purge(ref.current); };
  }, [traces, layout]);
  return <div ref={ref} style={{ width: "100%", height: 320 }} />;
}

function AnalyticsCharts({ data }) {
  const COLORS = ["#c47a10","#2ea043","#3b82f6","#a78bfa","#f59e0b","#ef4444","#06b6d4","#ec4899","#84cc16","#f97316","#8b5cf6","#14b8a6"];
  const safe = v => (v == null || isNaN(v)) ? 0 : Number(v);
  const fmt = v => { const n = safe(v); return n >= 1e6 ? `$${(n/1e6).toFixed(1)}M` : n >= 1e3 ? `$${(n/1e3).toFixed(0)}K` : `$${n.toFixed(0)}`; };
  const money = v => `$${safe(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const cardStyle = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: "20px 20px 12px" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

      {/* Row 1: Monthly Revenue + GP% */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div style={cardStyle}>
          <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 14, color: T.textSecondary }}>Monthly Revenue <span style={{ color: T.textMuted, fontWeight: 400 }}>— {new Date().getFullYear()}</span></p>
          <PlotlyChart id="monthly-rev" traces={[{
            type: "bar", x: data.monthlyRevenue.labels, y: data.monthlyRevenue.revenue,
            marker: { color: T.accent }, text: data.monthlyRevenue.revenue.map(fmt), textposition: "outside",
            hovertemplate: "%{x}: $%{y:,.0f}<extra></extra>",
          }]} layout={{ yaxis: { tickformat: "$,.0f" } }} />
        </div>
        <div style={cardStyle}>
          <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 14, color: T.textSecondary }}>Gross Profit % <span style={{ color: T.textMuted, fontWeight: 400 }}>— {new Date().getFullYear()}</span></p>
          <PlotlyChart id="gp-pct" traces={[{
            type: "scatter", mode: "lines+markers+text", x: data.monthlyRevenue.labels, y: data.monthlyRevenue.gp,
            line: { color: "#a78bfa", width: 2.5 }, marker: { color: "#a78bfa", size: 7 },
            text: data.monthlyRevenue.gp.map(v => v != null ? `${v}%` : ""), textposition: "top center",
            hovertemplate: "%{x}: %{y:.1f}%<extra></extra>",
            connectgaps: true,
          }]} layout={{ yaxis: { ticksuffix: "%", range: [0, 100] } }} />
        </div>
      </div>

      {/* Row 2: Top 10 Customers + Salesperson */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div style={cardStyle}>
          <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 14, color: T.textSecondary }}>Top 10 Customers <span style={{ color: T.textMuted, fontWeight: 400 }}>— YTD</span></p>
          <PlotlyChart id="top-customers" traces={[{
            type: "bar", orientation: "h",
            x: [...data.topCustomers.revenue].reverse(),
            y: [...data.topCustomers.names].reverse(),
            marker: { color: COLORS.slice(0, data.topCustomers.names.length) },
            hovertemplate: "%{y}: $%{x:,.0f}<extra></extra>",
          }]} layout={{ margin: { t: 10, r: 80, b: 40, l: 130 }, xaxis: { tickformat: "$,.0f" } }} />
        </div>
        <div style={cardStyle}>
          <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 14, color: T.textSecondary }}>Revenue by Salesperson <span style={{ color: T.textMuted, fontWeight: 400 }}>— YTD</span></p>
          <PlotlyChart id="salesperson" traces={[{
            type: "bar", x: data.salesperson.names, y: data.salesperson.revenue,
            marker: { color: data.salesperson.names.map((_, i) => COLORS[i % COLORS.length]) },
            text: data.salesperson.revenue.map(fmt), textposition: "outside",
            hovertemplate: "%{x}: $%{y:,.0f}<extra></extra>",
          }]} layout={{ yaxis: { tickformat: "$,.0f" } }} />
        </div>
      </div>

      {/* Row 3: Year-over-Year */}
      <div style={cardStyle}>
        <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 14, color: T.textSecondary }}>Year-over-Year Monthly Revenue</p>
        <PlotlyChart id="yoy" traces={data.yoy.series.map((s, i) => ({
          type: "scatter", mode: "lines+markers", name: String(s.year),
          x: data.yoy.months, y: s.values,
          line: { color: [T.accent, "#2ea043", "#3b82f6"][i], width: 2.5 },
          marker: { size: 5 },
          hovertemplate: `${s.year} %{x}: $%{y:,.0f}<extra></extra>`,
          connectgaps: true,
        }))} layout={{ height: 280, margin: { t: 10, r: 10, b: 40, l: 70 }, yaxis: { tickformat: "$,.0f" } }} />
      </div>

      {/* Row 4: Commission Payout Table */}
      <div style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary }}>
            Commission Payout — Paid Invoices <span style={{ color: T.textMuted, fontWeight: 400 }}>({data.commissions.length} records)</span>
          </p>
          <span style={{ fontSize: 12, color: T.textMuted }}>
            Total comm: <strong style={{ color: T.accent }}>{money(data.commissions.reduce((s, r) => s + safe(r.commAmt), 0))}</strong>
          </span>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                {["Invoice","Date","Customer","Salesperson","Sales Order","Invoice Amt","SO Value","% of SO","Comm %","Commission","Payment Date"].map(h => (
                  <th key={h} style={{ padding: "8px 12px", textAlign: h === "Invoice" || h === "Date" || h === "Customer" || h === "Salesperson" || h === "Sales Order" ? "left" : "right", fontSize: 10, fontWeight: 600, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.commissions.map((r, i) => {
                const pct = r.pctOfOrder;
                const pctColor = pct == null ? T.textMuted : pct >= 90 ? T.success : pct >= 50 ? T.warn : T.error;
                return (
                <tr key={i} style={{ borderBottom: `1px solid ${T.border}`, background: i % 2 === 0 ? "rgba(255,255,255,0.01)" : "transparent" }}>
                  <td style={{ padding: "7px 12px", color: T.textSecondary, fontFamily: "'JetBrains Mono',monospace", whiteSpace: "nowrap" }}>{r.invoiceNo}</td>
                  <td style={{ padding: "7px 12px", color: T.textMuted, whiteSpace: "nowrap" }}>{r.date}</td>
                  <td style={{ padding: "7px 12px", color: T.textPrimary, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.customer}</td>
                  <td style={{ padding: "7px 12px", color: T.textSecondary }}>{r.salesperson}</td>
                  <td style={{ padding: "7px 12px", color: T.textSecondary, fontFamily: "'JetBrains Mono',monospace", whiteSpace: "nowrap" }}>{r.salesOrderNo}</td>
                  <td style={{ padding: "7px 12px", color: T.textPrimary, textAlign: "right", fontFamily: "'JetBrains Mono',monospace", whiteSpace: "nowrap" }}>{money(r.amount)}</td>
                  <td style={{ padding: "7px 12px", color: T.textSecondary, textAlign: "right", fontFamily: "'JetBrains Mono',monospace", whiteSpace: "nowrap" }}>{r.orderAmount > 0 ? money(r.orderAmount) : "—"}</td>
                  <td style={{ padding: "7px 12px", textAlign: "right", fontWeight: 700, color: pctColor, whiteSpace: "nowrap" }}>{pct != null ? `${pct}%` : "—"}</td>
                  <td style={{ padding: "7px 12px", color: T.textMuted, textAlign: "right" }}>{safe(r.commRate)}%</td>
                  <td style={{ padding: "7px 12px", color: T.accent, textAlign: "right", fontWeight: 600, fontFamily: "'JetBrains Mono',monospace", whiteSpace: "nowrap" }}>{money(r.commAmt)}</td>
                  <td style={{ padding: "7px 12px", color: T.success, whiteSpace: "nowrap" }}>{r.paymentDate}</td>
                </tr>
                );
              })}
              {data.commissions.length === 0 && (
                <tr><td colSpan={11} style={{ padding: 24, textAlign: "center", color: T.textMuted }}>No paid commission records found for this year</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Sales Order Detail component ────────────────────────────────────────────
function SalesOrderDetail({ data }) {
  const { header, summary, lineItems, invoices, invoiceLines, videos, documents, listDocs } = data;
  const [activeVideo, setActiveVideo] = useState(null);
  const fmt = v => `$${(v||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-US') : '—';
  const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: "18px 20px" };
  const th = { padding: "8px 12px", textAlign: "left", fontSize: 10, fontWeight: 600, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap" };
  const td = { padding: "7px 12px", fontSize: 12, borderBottom: `1px solid ${T.border}` };

  // Plot qty chart using window.Plotly
  const soChartRef = useRef(null);
  const invChartRef = useRef(null);

  useEffect(() => {
    if (!soChartRef.current || !window.Plotly || !lineItems.length) return;
    const validLines = lineItems.filter(l => l.qtyOrdered > 0).slice(0, 15);
    const labels = validLines.map(l => (l.itemCode||'').slice(0,14));
    window.Plotly.newPlot(soChartRef.current, [
      { name: 'Ordered', x: labels, y: validLines.map(l => l.qtyOrdered), type: 'bar', marker: { color: 'rgba(196,122,16,0.7)' } },
      { name: 'Shipped', x: labels, y: validLines.map(l => l.qtyShipped), type: 'bar', marker: { color: 'rgba(46,160,67,0.8)' } },
      { name: 'Backorder', x: labels, y: validLines.map(l => l.qtyBackordered), type: 'bar', marker: { color: 'rgba(248,81,73,0.7)' } },
    ], {
      paper_bgcolor: T.surface, plot_bgcolor: T.surface, barmode: 'group',
      font: { color: T.textPrimary, size: 10 },
      xaxis: { gridcolor: T.border, tickangle: -35 },
      yaxis: { gridcolor: T.border, title: 'Qty' },
      legend: { orientation: 'h', y: 1.15 },
      margin: { t: 10, b: 80, l: 50, r: 10 },
      hoverlabel: { bgcolor: '#1c2128', bordercolor: '#c47a10', font: { color: '#e6edf3', size: 12 } },
    }, { displayModeBar: false, responsive: true });
  }, [lineItems]);

  useEffect(() => {
    if (!invChartRef.current || !window.Plotly || !invoices.length) return;
    const labels = invoices.map(i => i.invoiceNo);
    window.Plotly.newPlot(invChartRef.current, [
      { name: 'Invoice Value', x: labels, y: invoices.map(i => i.value), type: 'bar', marker: { color: 'rgba(46,160,67,0.8)' },
        text: invoices.map(i => `$${(i.value/1000).toFixed(0)}K`), textposition: 'outside',
        hovertemplate: '%{x}<br>$%{y:,.0f}<extra></extra>' },
    ], {
      paper_bgcolor: T.surface, plot_bgcolor: T.surface,
      font: { color: T.textPrimary, size: 10 },
      xaxis: { gridcolor: T.border },
      yaxis: { gridcolor: T.border, tickformat: '$,.0f' },
      legend: { orientation: 'h', y: 1.15 },
      margin: { t: 30, b: 50, l: 70, r: 10 },
      shapes: [{ type: 'line', x0: -0.5, x1: labels.length - 0.5, y0: summary.totalOrdered, y1: summary.totalOrdered,
        line: { color: T.accent, width: 1.5, dash: 'dot' } }],
      annotations: [{ x: labels.length - 1, y: summary.totalOrdered, text: 'Order value', showarrow: false,
        font: { color: T.accent, size: 10 }, xanchor: 'right', yanchor: 'bottom' }],
      hoverlabel: { bgcolor: '#1c2128', bordercolor: '#c47a10', font: { color: '#e6edf3', size: 12 } },
    }, { displayModeBar: false, responsive: true });
  }, [invoices]);

  const statusColor = header.orderStatus === 'O' ? T.success : header.orderStatus === 'C' ? T.textMuted : T.warn;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* Header summary */}
      <div style={{ ...card, borderLeft: `4px solid ${T.accent}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: T.accent }}>SO {header.salesOrderNo}</h2>
              <span style={{ background: `${statusColor}22`, color: statusColor, border: `1px solid ${statusColor}44`, borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600 }}>
                {header.orderStatus === 'O' ? 'Open' : header.orderStatus === 'C' ? 'Completed' : header.orderStatus || 'Unknown'}
              </span>
              {data.fromHistory && <span style={{ background: T.accentGlow2, color: T.accent, border: `1px solid ${T.accent}44`, borderRadius: 20, padding: "2px 10px", fontSize: 10 }}>History</span>}
              {data.fromInvoicesOnly && <span style={{ background: "rgba(59,130,246,.15)", color: "#3b82f6", border: "1px solid rgba(59,130,246,.3)", borderRadius: 20, padding: "2px 10px", fontSize: 10 }}>From AR Invoices</span>}
            </div>
            <p style={{ fontSize: 15, fontWeight: 600, color: T.textPrimary, marginBottom: 4 }}>{header.billToName || header.customerName}</p>
            {header.projectName && <p style={{ fontSize: 13, color: T.textSecondary }}>Project: {header.projectName}</p>}
            {header.customerPONo && <p style={{ fontSize: 12, color: T.textMuted }}>Customer PO: {header.customerPONo}</p>}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 24px", textAlign: "right" }}>
            {[
              ["Order Date", fmtDate(header.orderDate)],
              ["Salesperson", header.salespersonNo || "—"],
              ["Order Value", fmt(header.orderValue)],
              ["Invoiced", fmt(summary.totalInvoiced)],
              ["GP%", `${summary.gpPct}%`],
              ["Fill Rate", `${summary.fillRate}%`],
            ].map(([label, value]) => (
              <div key={label}>
                <p style={{ fontSize: 10, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</p>
                <p style={{ fontSize: 14, fontWeight: 600, color: T.textPrimary, fontFamily: label.includes('Date') || label.includes('person') ? undefined : "'JetBrains Mono',monospace" }}>{value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* KPI row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12 }}>
        {[
          { label: "Line Items", value: summary.lineCount, color: T.accent },
          { label: "Invoices", value: summary.invoiceCount, color: T.success },
          { label: "Ordered Qty", value: summary.totalOrderedQty, color: T.warn },
          { label: "Shipped Qty", value: summary.totalShippedQty, color: "#3b82f6" },
        ].map(k => (
          <div key={k.label} style={{ ...card, textAlign: "center" }}>
            <p style={{ fontSize: 10, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{k.label}</p>
            <p style={{ fontSize: 26, fontWeight: 700, color: k.color, fontFamily: "'JetBrains Mono',monospace" }}>{k.value}</p>
          </div>
        ))}
      </div>

      {/* Charts row */}
      {lineItems.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <div style={card}>
            <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 12 }}>Qty: Ordered vs Shipped vs Backorder</p>
            <div ref={soChartRef} style={{ height: 280 }} />
          </div>
          <div style={card}>
            <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 12 }}>Invoices vs Order Value</p>
            {invoices.length > 0
              ? <div ref={invChartRef} style={{ height: 280 }} />
              : <div style={{ height: 280, display: "flex", alignItems: "center", justifyContent: "center", color: T.textMuted, fontSize: 13 }}>No invoices yet</div>}
          </div>
        </div>
      )}

      {/* Line Items table */}
      {lineItems.length > 0 && (
        <div style={card}>
          <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 14 }}>Line Items ({lineItems.length})</p>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead><tr>
                {["Item Code","Description","Qty Ordered","Qty Shipped","Backorder","Unit Price","Amount","Promise Date","Comment"].map(h => (
                  <th key={h} style={{ ...th, textAlign: h === "Description" || h === "Item Code" || h === "Comment" ? "left" : "right" }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>{lineItems.map((l, i) => (
                <tr key={i} style={{ background: i%2===0 ? "rgba(255,255,255,.01)" : "transparent" }}>
                  <td style={{ ...td, fontFamily: "'JetBrains Mono',monospace", color: T.accent }}>{l.itemCode}</td>
                  <td style={{ ...td, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.description}</td>
                  <td style={{ ...td, textAlign: "right" }}>{l.qtyOrdered}</td>
                  <td style={{ ...td, textAlign: "right", color: l.qtyShipped > 0 ? T.success : T.textMuted }}>{l.qtyShipped}</td>
                  <td style={{ ...td, textAlign: "right", color: l.qtyBackordered > 0 ? T.error : T.textMuted }}>{l.qtyBackordered || "—"}</td>
                  <td style={{ ...td, textAlign: "right", fontFamily: "'JetBrains Mono',monospace" }}>{fmt(l.unitPrice)}</td>
                  <td style={{ ...td, textAlign: "right", fontFamily: "'JetBrains Mono',monospace", fontWeight: 600 }}>{fmt(l.extAmount)}</td>
                  <td style={{ ...td, textAlign: "right", color: T.textMuted, whiteSpace: "nowrap" }}>{fmtDate(l.promiseDate)}</td>
                  <td style={{ ...td, color: T.textMuted, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.comment || "—"}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}

      {/* Invoices table */}
      {invoices.length > 0 && (
        <div style={card}>
          <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 14 }}>Invoices ({invoices.length}) — Total {fmt(summary.totalInvoiced)}</p>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead><tr>
              {["Invoice #","Date","Due Date","Value","Cost","Freight","Commission","Salesperson"].map(h => (
                <th key={h} style={{ ...th, textAlign: h==="Invoice #" || h==="Salesperson" ? "left" : "right" }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>{invoices.map((inv, i) => (
              <tr key={i} style={{ background: i%2===0 ? "rgba(255,255,255,.01)" : "transparent" }}>
                <td style={{ ...td, fontFamily: "'JetBrains Mono',monospace", color: T.success }}>{inv.invoiceNo}</td>
                <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap", color: T.textMuted }}>{fmtDate(inv.invoiceDate)}</td>
                <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap", color: T.textMuted }}>{fmtDate(inv.dueDate)}</td>
                <td style={{ ...td, textAlign: "right", fontFamily: "'JetBrains Mono',monospace", fontWeight: 600 }}>{fmt(inv.value)}</td>
                <td style={{ ...td, textAlign: "right", fontFamily: "'JetBrains Mono',monospace", color: T.textMuted }}>{fmt(inv.cost)}</td>
                <td style={{ ...td, textAlign: "right", color: T.textMuted }}>{fmt(inv.freight)}</td>
                <td style={{ ...td, textAlign: "right", color: T.accent }}>{fmt(inv.commission)}</td>
                <td style={{ ...td, color: T.textSecondary }}>{inv.salesperson || "—"}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {/* YouTube Videos — inline player */}
      {videos.length > 0 && (
        <div style={card}>
          <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 14 }}>
            <Icon n="external" size={14} color="#ef4444" /> YouTube Videos ({videos.length})
          </p>
          {/* Inline player */}
          {activeVideo && (
            <div style={{ marginBottom: 16, borderRadius: 10, overflow: "hidden", background: "#000", position: "relative" }}>
              <iframe
                src={`https://www.youtube.com/embed/${activeVideo}?autoplay=1`}
                style={{ width: "100%", height: 420, border: "none", display: "block" }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen title="YouTube Video" />
              <button onClick={() => setActiveVideo(null)}
                style={{ position: "absolute", top: 8, right: 8, background: "rgba(0,0,0,.7)", border: "none", borderRadius: 6, color: "#fff", cursor: "pointer", padding: "4px 10px", fontSize: 12 }}>✕ Close</button>
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
            {videos.map((v, i) => (
              <div key={i} onClick={() => setActiveVideo(activeVideo === v.id ? null : v.id)}
                style={{ background: T.bg, border: `2px solid ${activeVideo === v.id ? T.accent : T.border}`, borderRadius: 10, overflow: "hidden", cursor: "pointer", transition: "border-color .2s, transform .15s" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = activeVideo === v.id ? T.accent : T.border; e.currentTarget.style.transform = "none"; }}>
                <div style={{ position: "relative" }}>
                  {v.thumbnail && <img src={v.thumbnail} alt={v.title} style={{ width: "100%", aspectRatio: "16/9", objectFit: "cover", display: "block" }} />}
                  <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,.3)", opacity: 0, transition: "opacity .2s" }}
                    onMouseEnter={e => e.currentTarget.style.opacity = "1"}
                    onMouseLeave={e => e.currentTarget.style.opacity = "0"}>
                    <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#ef4444", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg width={18} height={18} viewBox="0 0 24 24" fill="#fff"><polygon points="5,3 19,12 5,21"/></svg>
                    </div>
                  </div>
                </div>
                <div style={{ padding: "8px 10px" }}>
                  <p style={{ fontSize: 11, fontWeight: 600, color: T.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v.title}</p>
                  <p style={{ fontSize: 10, color: T.textMuted, marginTop: 2 }}>
                    {v.publishedAt ? new Date(v.publishedAt).toLocaleDateString('en-US') : ''}
                    {v.viewCount ? ` · ${parseInt(v.viewCount).toLocaleString()} views` : ''}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Documents — manufacturing + SharePoint list */}
      {(documents.length > 0 || (listDocs && listDocs.length > 0)) && (
        <div style={card}>
          <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 14 }}>
            <Icon n="db" size={14} /> Documents & Files
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {[...documents, ...(listDocs||[])].map((doc, i) => (
              <a key={i} href={doc.url} target="_blank" rel="noopener noreferrer"
                style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 14px", textDecoration: "none", display: "flex", alignItems: "flex-start", gap: 10, transition: "border-color .2s" }}
                onMouseEnter={e => e.currentTarget.style.borderColor = T.accent}
                onMouseLeave={e => e.currentTarget.style.borderColor = T.border}>
                <span style={{ fontSize: 18, flexShrink: 0 }}>
                  {(doc.name||'').endsWith('.pdf') ? '📄' : (doc.name||'').match(/\.(dwg|dxf|stp|step)$/i) ? '📐' : (doc.name||'').match(/\.(xlsx|csv)$/i) ? '📊' : '📁'}
                </span>
                <div style={{ minWidth: 0 }}>
                  <p style={{ fontSize: 13, color: T.textPrimary, fontWeight: 500, marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.name}</p>
                  {doc.source && <span style={{ fontSize: 10, color: T.textMuted, background: T.border, borderRadius: 4, padding: "1px 6px" }}>{doc.source}</span>}
                  {doc.preview && <p style={{ fontSize: 10, color: T.textMuted, marginTop: 4, fontFamily: "'JetBrains Mono',monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.preview.slice(0, 80)}</p>}
                </div>
                <Icon n="external" size={13} color={T.textMuted} />
              </a>
            ))}
          </div>
        </div>
      )}

      {videos.length === 0 && documents.length === 0 && (!listDocs || listDocs.length === 0) && (
        <div style={{ ...card, textAlign: "center", color: T.textMuted, padding: 24 }}>
          <Icon n="external" size={20} color={T.border} />
          <p style={{ fontSize: 12, marginTop: 10 }}>No related videos or documents found for this order</p>
        </div>
      )}
    </div>
  );
}

// ─── Alternative Reports config ───────────────────────────────────────────────
// tenantMatch: substring of the tenant name as stored in the DB (case-insensitive)
const ALT_REPORTS = [
  { id: "rdp-financials-q1-2026", name: "RDP Financials Q1 2026", description: "Raindrop Products financial summary — Q1 2026", type: "html", file: "/reports/RDP_Financials_Q1_2026.html", tenant: "RDP", tenantMatch: "rain-drop" },
  { id: "analytics-dashboard", name: "Analytics Dashboard", description: "Live charts — monthly revenue, GP%, top customers, salesperson & YoY, plus commission payout report", type: "analytics", tenant: "RDP", tenantMatch: "rain-drop" },
  { id: "eew-executive-sales", name: "Executive Sales Report", description: "East End Welding — monthly sales, YoY trends, top customers, open orders & salesperson breakdown", type: "html", file: "/reports/EEW_Executive_Sales_Report.html?v=5", tenant: "EEW", tenantMatch: "east end" },
];

function Dashboard({ user, token, onLogout }) {
  const [nav, setNav] = useState("reports");
  const [altReport, setAltReport] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [soQuery, setSoQuery] = useState("");
  const [soData, setSoData] = useState(null);
  const [soLoading, setSoLoading] = useState(false);
  const [soErr, setSoErr] = useState("");
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsErr, setAnalyticsErr] = useState("");
  const [reports, setReports] = useState([]);
  const [tenantName, setTenantName] = useState("");
  const [loadingReports, setLoadingReports] = useState(true);
  const [reportsErr, setReportsErr] = useState("");
  const [activeReport, setActiveReport] = useState(null);
  const [embedData, setEmbedData] = useState(null);
  const [loadingEmbed, setLoadingEmbed] = useState(false);
  const [embedErr, setEmbedErr] = useState("");
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("All");
const [messages, setMessages] = useState([]);
const [chatHistory, setChatHistory] = useState([]);
const [savedQueries, setSavedQueries] = useState([]);
const [showHistory, setShowHistory] = useState(false);
const [alerts, setAlerts] = useState([]);
const [schedules, setSchedules] = useState([]);
const [auditLogs, setAuditLogs] = useState([]);
const [reportComments, setReportComments] = useState([]);
const [newComment, setNewComment] = useState("");
const [showAlertModal, setShowAlertModal] = useState(false);
const [showScheduleModal, setShowScheduleModal] = useState(false);
const [newAlert, setNewAlert] = useState({ name:"", question:"", condition:"gt", threshold:"", notifyEmail:"", frequency:"daily" });
const [newSchedule, setNewSchedule] = useState({ reportName:"", question:"", recipients:"", frequency:"weekly", dayOfWeek:1, hour:8 });
const [kpiCards, setKpiCards] = useState([]);
const [kpiLoading, setKpiLoading] = useState(false);
const [defaultKpiCards, setDefaultKpiCards] = useState([]);
const [defaultKpiLoading, setDefaultKpiLoading] = useState(false);
const [viewAs, setViewAs] = useState(null);
const [exportingFormat, setExportingFormat] = useState(null);
const [showShareModal, setShowShareModal] = useState(false);
const [shareEmail, setShareEmail] = useState("");
const [shareNote, setShareNote] = useState("");
const [shareSending, setShareSending] = useState(false);
const pbiReportRef = useRef(null);
const [expandedTenant, setExpandedTenant] = useState(null);
const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
const [showAddUser, setShowAddUser] = useState(false);
const [addUserTenantId, setAddUserTenantId] = useState("");
const [addUserTenantName, setAddUserTenantName] = useState("");
const [newUser, setNewUser] = useState({ name: "", email: "", role: "VIEWER" });
const [inviteLoading, setInviteLoading] = useState(false);
const [adminData, setAdminData] = useState(null);
const [showAddTenant, setShowAddTenant] = useState(false);
const inviteUser = async () => {
  if (!newUser.name || !newUser.email) return;
  setInviteLoading(true);
  try {
    const res = await fetch(`${API_BASE}/admin/users`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, "Content-Type": "application/json" },
      body: JSON.stringify({ tenantId: addUserTenantId, ...newUser }),
    });
    const data = await res.json();
    if (data.error) throw new Error(data.error);
    setShowAddUser(false);
    setNewUser({ name: "", email: "", role: "VIEWER" });
    loadAdminData();
    alert(`✅ Invitation sent to ${newUser.email}!`);
  } catch (err) {
    alert(`❌ Error: ${err.message}`);
  } finally {
    setInviteLoading(false);
  }
};
const [newTenant, setNewTenant] = useState({
  name: "",
  slug: "",
  azureTenantId: "",
  pbiWorkspaceId: "",
  dbType: "client",
  dbConnectionString: "",
  dbSchema: "public"
});
const [input, setInput] = useState("");
const [aiLoading, setAiLoading] = useState(false);
const messagesEndRef = useRef(null);
useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, aiLoading]);

// Deep-link routing: portal.powervisionbi.com/#report/{id}
useEffect(() => {
  const hash = window.location.hash;
  if (!hash.startsWith('#report/')) return;
  const reportId = decodeURIComponent(hash.replace('#report/', ''));
  if (!reportId || !reports.length) return;
  const found = reports.find(r => r.pbiReportId === reportId || r.id === reportId);
  if (found) { setNav("reports"); openReport(found); window.location.hash = ''; }
}, [reports]);
const loadAdminData = async () => {
  try {
    const res = await fetch(`${API_BASE}/admin/tenants`, {
      headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}` }
    });
    const data = await res.json();
    setAdminData(data);
  } catch (err) {
    console.error("Admin data error:", err);
  }
};

// ── Load helpers for new features ────────────────────────────────────────────
const loadChatHistory = async () => {
  try { const d = await (await fetch(`${API_BASE}/chat-history`, { headers: authHeaders(token, viewAs?.id) })).json(); setChatHistory(d.messages || []); } catch {}
};
const loadSavedQueries = async () => {
  try { const d = await (await fetch(`${API_BASE}/saved-queries`, { headers: authHeaders(token, viewAs?.id) })).json(); setSavedQueries(d.queries || []); } catch {}
};
const loadAlerts = async () => {
  try { const d = await (await fetch(`${API_BASE}/alerts`, { headers: authHeaders(token, viewAs?.id) })).json(); setAlerts(d.alerts || []); } catch {}
};
const loadSchedules = async () => {
  try { const d = await (await fetch(`${API_BASE}/scheduled-reports`, { headers: authHeaders(token, viewAs?.id) })).json(); setSchedules(d.schedules || []); } catch {}
};
const loadAuditLogs = async () => {
  try { const d = await (await fetch(`${API_BASE}/audit-log`, { headers: authHeaders(token) })).json(); setAuditLogs(d.logs || []); } catch {}
};
const loadComments = async (pbiReportId) => {
  try { const d = await (await fetch(`${API_BASE}/comments?reportId=${encodeURIComponent(pbiReportId)}`, { headers: authHeaders(token, viewAs?.id) })).json(); setReportComments(d.comments || []); } catch {}
};
const logAction = async (action, detail) => {
  try { await fetch(`${API_BASE}/audit-log`, { method: "POST", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify({ action, detail }) }); } catch {}
};
const saveMessage = async (role, content, sql, rowCount) => {
  try { await fetch(`${API_BASE}/chat-history`, { method: "POST", headers: { ...authHeaders(token, viewAs?.id), "Content-Type": "application/json" }, body: JSON.stringify({ role, content, sql, rowCount }) }); } catch {}
};
const DEFAULT_KPIS = [
  { id: 'ytd-rev',    name: 'YTD Revenue',       question: 'What is the total Year-to-Date revenue or net sales amount?',                          color: '#c47a10', icon: 'chart' },
  { id: 'mtd-rev',    name: 'MTD Revenue',       question: 'What is the total Month-to-Date revenue or net sales amount for the current month?',   color: '#c47a10', icon: 'chart' },
  { id: 'ytd-bud',    name: 'YTD Budget',        question: 'What is the Year-to-Date budget or target sales amount?',                              color: '#2ea043', icon: 'zap'   },
  { id: 'mtd-bud',    name: 'MTD Budget',        question: 'What is the Month-to-Date budget or target sales amount for the current month?',       color: '#2ea043', icon: 'zap'   },
  { id: 'ytd-gp',     name: 'YTD GP%',           question: 'What is the Year-to-Date gross profit percentage?',                                    color: '#a78bfa', icon: 'db'    },
  { id: 'mtd-gp',     name: 'MTD GP%',           question: 'What is the Month-to-Date gross profit percentage for the current month?',             color: '#a78bfa', icon: 'db'    },
  { id: 'open-ord',   name: 'Open Orders',       question: 'What is the total COUNT (number) of open confirmed sales orders? Return only the count, not a dollar amount.',  color: '#f59e0b', icon: 'grid'  },
  { id: 'open-ord-val', name: 'Open Order Balance', question: 'What is the total value or dollar amount of all open sales orders?',               color: '#38bdf8', icon: 'send'  },
  { id: 'open-inv',   name: 'Open Invoices',     question: 'What is the total outstanding balance or dollar amount owed on all open unpaid customer invoices?', color: '#f87171', icon: 'alert' },
];

const loadDefaultKPIs = async (noCache = false) => {
  setDefaultKpiLoading(true);
  setDefaultKpiCards([]);
  const results = [];
  for (const kpi of DEFAULT_KPIS) {
    try {
      const res = await fetch(`${API_BASE}/ai-query`, { method: "POST", headers: { ...authHeaders(token, viewAs?.id), "Content-Type": "application/json" }, body: JSON.stringify({ question: kpi.question, noCache }) });
      const d = await res.json();
      results.push({ ...kpi, answer: d.answer });
    } catch {
      results.push({ ...kpi, answer: null });
    }
    setDefaultKpiCards([...results]);
  }
  setDefaultKpiLoading(false);
};

const loadHomeKPIs = async () => {
  if (!savedQueries.length) return;
  setKpiLoading(true);
  const cards = [];
  for (const q of savedQueries.slice(0, 4)) {
    try {
      const res = await fetch(`${API_BASE}/ai-query`, { method: "POST", headers: { ...authHeaders(token, viewAs?.id), "Content-Type": "application/json" }, body: JSON.stringify({ question: q.question }) });
      const d = await res.json();
      cards.push({ name: q.name, question: q.question, answer: d.answer, rowCount: d.rowCount });
    } catch {}
  }
  setKpiCards(cards);
  setKpiLoading(false);
};

const addTenant = async () => {
  try {
    await fetch(`${API_BASE}/admin/tenants`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, "Content-Type": "application/json" },
      body: JSON.stringify(newTenant)
    });
    setShowAddTenant(false);
    setNewTenant({ name: "", slug: "", azureTenantId: "", pbiWorkspaceId: "", dbType: "client", dbConnectionString: "", dbSchema: "public" });
    loadAdminData();
  } catch (err) {
    console.error("Add tenant error:", err);
  }
};
const sendMessage = async (override) => {
  const question = (override ?? input).trim();
  if (!question || aiLoading) return;
  if (!override) setInput("");
  const userMsg = { id: Date.now(), role: "user", content: question };
  const history = [...messages, userMsg];
  setMessages(history);
  saveMessage("user", question);
  sendAiRequest(question, history);
};

const sendAiRequest = async (question, history) => {
  setAiLoading(true);
  try {
    const res = await fetch(`${API_BASE}/ai-query`, {
      method: "POST",
      headers: { ...authHeaders(token, viewAs?.id), "Content-Type": "application/json" },
      body: JSON.stringify({ question, history: history.map(m => ({ role: m.role, content: m.content })) }),
    });
    const data = await res.json();
    const assistantMsg = { id: Date.now(), role: "assistant", content: data.answer || "Sorry, I could not answer that.", chart: data.chart || null, exportData: data.exportData || null, question };
    setMessages(prev => [...prev, assistantMsg]);
    saveMessage("assistant", assistantMsg.content, data.sql, data.rowCount);
    logAction("ai_query", question);
  } catch {
    setMessages(prev => [...prev, { id: Date.now(), role: "assistant", content: "Sorry, something went wrong. Please try again." }]);
  } finally {
    setAiLoading(false);
  }
};
 useEffect(() => {
  let cancelled = false;
  async function load() {
    setLoadingReports(true); setReportsErr("");
    try {
      const data = await fetchReports(token, viewAs?.id);
      if (!cancelled) {
        setReports(data.reports || []);
        setTenantName(data.tenant?.name || "");
      }
    } catch (e) {
      if (!cancelled) setReportsErr("Failed to load reports. Please refresh or contact support.");
    } finally {
      if (!cancelled) setLoadingReports(false);
    }
    // Load admin data if admin user
    if (user.email === "christiaan.horn@powervisionbi.com") {
      loadAdminData();
    }
  }
  load();
  return () => { cancelled = true; };
}, [token, viewAs]);

// Load feature data on mount
useEffect(() => {
  if (!token) return;
  loadSavedQueries();
  loadChatHistory();
  loadAlerts();
  loadSchedules();
}, [token, viewAs]);

// Load comments when report opens
useEffect(() => {
  if (activeReport) { loadComments(activeReport.pbiReportId || activeReport.id); logAction('view_report', activeReport.name); }
}, [activeReport]);

// Load KPIs when on home tab
useEffect(() => {
  if (nav === 'home') {
    loadDefaultKPIs();
    if (savedQueries.length) loadHomeKPIs();
  }
}, [nav]);
useEffect(() => { if (nav === 'audit') loadAuditLogs(); }, [nav]);
useEffect(() => { if (nav === 'alerts') loadAlerts(); }, [nav]);
useEffect(() => { if (nav === 'schedule') loadSchedules(); }, [nav]);

  // Open a report — fetch embed token from backend
  const openReport = useCallback(async (report) => {
    setActiveReport(report);
    setEmbedData(null);
    setEmbedErr("");
    setLoadingEmbed(true);
    try {
      const data = await fetchEmbedToken(token, report.id, viewAs?.id);
      setEmbedData(data);
    } catch (e) {
      setEmbedErr("Could not load this report. Please try again.");
    } finally {
      setLoadingEmbed(false);
    }
  }, [token, viewAs]);

  const activeTenantName = (viewAs?.name || tenantName || "").toLowerCase();
  const altReportsForTenant = ALT_REPORTS.filter(r => activeTenantName.includes(r.tenantMatch));

  const cats = ["All", ...Array.from(new Set(reports.map(r => r.cat || "Other")))];
  const filtered = reports.filter(r => {
    const ms = r.name.toLowerCase().includes(search.toLowerCase()) || (r.description || "").toLowerCase().includes(search.toLowerCase());
    return ms && (cat === "All" || r.cat === cat);
  });
  const initials = (user.name || "U").slice(0, 2).toUpperCase();

  return (
    <div style={{ display: "flex", minHeight: "100vh", position: "relative", zIndex: 1 }}>
      {/* Mobile overlay */}
      <div className={`sidebar-overlay${mobileSidebarOpen ? " open" : ""}`} onClick={() => setMobileSidebarOpen(false)} />
      {/* Fixed mobile hamburger — always visible on small screens */}
      {!mobileSidebarOpen && (
        <button className="hamburger-btn" onClick={() => setMobileSidebarOpen(true)}
          style={{ position: "fixed", bottom: 20, right: 20, zIndex: 300, width: 48, height: 48, borderRadius: "50%", background: T.accent, border: "none", color: "#fff", boxShadow: "0 4px 16px rgba(0,0,0,.4)" }}>
          <Icon n="menu" size={20} color="#fff" />
        </button>
      )}
      {/* Sidebar */}
      <div className={`mob-sidebar${mobileSidebarOpen ? " open" : ""}`} style={{ width: 224, background: T.surface, borderRight: `1px solid ${T.border}`, display: "flex", flexDirection: "column", padding: "18px 12px", flexShrink: 0 }}>
        <div style={{ padding: "4px 8px 8px 8px", marginBottom: 16 }}>
          <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: "-0.02em", color: T.textPrimary }}>
            Power<span style={{ color: T.accent }}>Vision</span> BI
          </div>
          <p style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>We Make Data Dance</p>
        </div>
        <div style={{ height: 1, background: `linear-gradient(90deg, ${T.accent}60, transparent)`, marginBottom: 20, marginLeft: 8, marginRight: 8 }} />

        <nav style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
          {[
{ id: "home", icon: "home", label: "Home" },
{ id: "reports", icon: "grid", label: "Reports" },
...(altReportsForTenant.length > 0 ? [{ id: "alt-reports", icon: "external", label: "Alt Reports" }] : []),
...(activeTenantName.includes("rain-drop") ? [{ id: "order-lookup", icon: "db", label: "Order Lookup" }] : []),
{ id: "ai", icon: "zap", label: "AI Agent" },
{ id: "alerts", icon: "alert", label: "Alerts" },
{ id: "schedule", icon: "refresh", label: "Scheduled" },
{ id: "audit", icon: "eye", label: "Audit Log" },
{ id: "settings", icon: "settings", label: "Settings" },
...(user.email === "christiaan.horn@powervisionbi.com" ? [{ id: "admin", icon: "shield", label: "Admin" }] : []),
].map(item => (
            <button key={item.id} className={`nav-item ${nav === item.id ? "active" : ""}`} onClick={() => { setNav(item.id); setActiveReport(null); setAltReport(null); setMobileSidebarOpen(false); }}>
              <Icon n={item.icon} size={15} />
              {item.label}
            </button>
          ))}
        </nav>

        {/* User */}
        <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "10px 8px", borderTop: `1px solid ${T.border}`, paddingTop: 14 }}>
          <div style={{ width: 30, height: 30, borderRadius: "50%", background: `linear-gradient(135deg, ${T.accent}, ${T.accentDark})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{initials}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.name}</p>
            <p style={{ fontSize: 10, color: T.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.email}</p>
          </div>
          <button onClick={onLogout} style={{ background: "none", border: "none", cursor: "pointer", color: T.textMuted, display: "flex", padding: 4 }} title="Sign out">
            <Icon n="logout" size={14} />
          </button>
        </div>
      </div>

      {/* Impersonation banner */}
      {viewAs && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 999, background: `linear-gradient(90deg, ${T.accent}, ${T.accentDark})`, padding: "8px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>
            👁 Viewing as: <strong>{viewAs.name}</strong>
          </span>
          <button
            onClick={() => { setViewAs(null); setMessages([]); setActiveReport(null); setNav("reports"); }}
            style={{ background: "rgba(255,255,255,.2)", border: "none", borderRadius: 6, padding: "4px 14px", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            ✕ Exit client view
          </button>
        </div>
      )}

      {/* Main */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", marginTop: viewAs ? 36 : 0 }}>

        {/* ── Home Dashboard ── */}
        {nav === "home" && (
          <div style={{ flex: 1, overflow: "auto", padding: "32px 36px" }}>

            {/* Header */}
            <div className="mob-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <button className="hamburger-btn" onClick={() => setMobileSidebarOpen(true)}><Icon n="menu" size={18} /></button>
                <div>
                  <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>
                    Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 17 ? "afternoon" : "evening"}, {user.name?.split(" ")[0] || user.name}
                  </h1>
                  <p style={{ fontSize: 13, color: T.textSecondary }}>Live business KPIs from your connected database</p>
                </div>
              </div>
              <button onClick={() => { loadDefaultKPIs(true); if (savedQueries.length) loadHomeKPIs(); }}
                disabled={defaultKpiLoading} style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 16px", color: T.textSecondary, cursor: "pointer", fontSize: 12, fontFamily: "'Plus Jakarta Sans', sans-serif", display: "flex", alignItems: "center", gap: 6 }}>
                <Icon n="refresh" size={13} />{defaultKpiLoading ? "Loading…" : "Refresh all"}
              </button>
            </div>

            {/* ── Default KPI grid ── */}
            <div className="kpi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 36 }}>
              {DEFAULT_KPIS.map(kpi => {
                const card = defaultKpiCards.find(c => c.id === kpi.id);
                const val = card ? extractKpiValue(card.answer) : null;
                return (
                  <div key={kpi.id} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: "20px 22px 16px", position: "relative", overflow: "hidden", transition: "border-color .2s, box-shadow .2s" }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = kpi.color; e.currentTarget.style.boxShadow = `0 6px 24px ${kpi.color}20`; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.boxShadow = "none"; }}>
                    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: kpi.color, borderRadius: "14px 14px 0 0" }} />
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                      <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: T.textMuted }}>{kpi.name}</p>
                      <div style={{ color: kpi.color, opacity: 0.7 }}><Icon n={kpi.icon} size={15} /></div>
                    </div>
                    {defaultKpiLoading && !card ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, height: 44 }}>
                        <span className="spinner spin" style={{ width: 15, height: 15, borderTopColor: kpi.color, borderColor: `${kpi.color}33` }} />
                        <span style={{ fontSize: 12, color: T.textMuted }}>Loading…</span>
                      </div>
                    ) : (
                      <p style={{ fontSize: 30, fontWeight: 700, color: kpi.color, lineHeight: 1.1, fontFamily: "'JetBrains Mono', monospace" }}>
                        {val || "—"}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* ── Custom KPI cards (saved queries) ── */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary }}>My KPIs <span style={{ fontWeight: 400, color: T.textMuted }}>— saved from AI Agent</span></p>
              <button className="btn-secondary" style={{ fontSize: 12, padding: "6px 14px" }} onClick={() => setNav("ai")}>+ Add KPI</button>
            </div>
            {savedQueries.length === 0 ? (
              <div style={{ background: T.surface, border: `1px dashed ${T.border}`, borderRadius: 12, padding: "28px 24px", textAlign: "center", color: T.textMuted }}>
                <Icon n="starEmpty" size={22} color={T.border} />
                <p style={{ fontSize: 13, marginTop: 10 }}>No custom KPIs yet</p>
                <p style={{ fontSize: 12, marginTop: 4 }}>Ask the AI Agent anything, then click ☆ to pin it here</p>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: 14 }}>
                {savedQueries.map((q, i) => {
                  const card = kpiCards.find(c => c.question === q.question);
                  const CARD_COLORS = [T.accent, T.success, "#a78bfa", T.warn, "#f87171", "#38bdf8"];
                  const color = CARD_COLORS[i % CARD_COLORS.length];
                  const val = card ? extractKpiValue(card.answer) : null;
                  return (
                    <div key={q.id} onClick={() => setNav("ai")} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: "20px 20px 16px", cursor: "pointer", transition: "border-color .2s, box-shadow .2s, transform .2s", position: "relative", overflow: "hidden" }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = color; e.currentTarget.style.boxShadow = `0 6px 24px ${color}22`; e.currentTarget.style.transform = "translateY(-2px)"; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "none"; }}>
                      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: color, borderRadius: "14px 14px 0 0" }} />
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                        <p style={{ fontSize: 11, color: T.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>{q.name}</p>
                        <button onClick={async e => { e.stopPropagation(); e.preventDefault(); const res = await fetch(`${API_BASE}/saved-queries`, { method: "DELETE", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify({ id: q.id }) }); if (res.ok) setSavedQueries(prev => prev.filter(sq => sq.id !== q.id)); }} style={{ background: "none", border: "none", cursor: "pointer", color: T.textMuted, padding: 2 }}><Icon n="trash" size={13} /></button>
                      </div>
                      {kpiLoading && !card ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, height: 44 }}>
                          <span className="spinner spin" style={{ width: 14, height: 14, borderTopColor: color, borderColor: `${color}33` }} />
                          <span style={{ fontSize: 12, color: T.textMuted }}>Loading…</span>
                        </div>
                      ) : card ? (
                        <p style={{ fontSize: 28, fontWeight: 700, color, lineHeight: 1.1, fontFamily: "'JetBrains Mono', monospace" }}>
                          {val || "—"}
                        </p>
                      ) : (
                        <p style={{ fontSize: 12, color: T.textMuted, height: 44, display: "flex", alignItems: "center" }}>Click Refresh to load</p>
                      )}
                      <p style={{ fontSize: 11, color: T.textMuted, marginTop: 10, borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>{q.question}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {nav === "reports" && !activeReport && (
          <div style={{ flex: 1, overflow: "auto", padding: "32px" }}>
            <div className="fu" style={{ marginBottom: 28 }}>
              <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Your reports</h1>
              <p style={{ fontSize: 13, color: T.textSecondary }}>Live Power BI reports — click any report to open it</p>
            </div>

            {/* Stats */}
            <div className="fu1 mob-stats" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 13, marginBottom: 28 }}>
              {[
                { label: "Reports available", value: loadingReports ? "…" : reports.length, icon: "grid", color: T.accent },
                { label: "Tenant", value: viewAs?.name || tenantName || "Loading…", icon: "db", color: T.success },
                { label: "Session status", value: "Active", icon: "refresh", color: T.warn },
              ].map((s, i) => (
                <div key={i} className="stat-card" style={{ animationDelay: `${i * 0.08}s` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                      <p style={{ fontSize: 11, color: T.textMuted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 5 }}>{s.label}</p>
                      <p style={{ fontSize: 20, fontWeight: 600, color: s.color }}>{s.value}</p>
                    </div>
                    <div style={{ color: s.color, opacity: 0.7 }}><Icon n={s.icon} size={18} /></div>
                  </div>
                </div>
              ))}
            </div>

            {/* Error state */}
            {reportsErr && <div className="alert alert-error" style={{ marginBottom: 20 }}><Icon n="alert" size={15} color={T.error} />{reportsErr}</div>}

            {/* Loading state */}
            {loadingReports && (
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: 32, color: T.textSecondary }}>
                <span className="spinner spin" style={{ width: 20, height: 20, borderTopColor: T.accent, borderColor: `rgba(196,122,16,.3)` }} />
                Loading your reports…
              </div>
            )}

            {!loadingReports && !reportsErr && (
              <>
                {/* Filters */}
                <div className="fu2" style={{ display: "flex", gap: 10, marginBottom: 20, alignItems: "center", flexWrap: "wrap" }}>
                  <input className="field-input" style={{ maxWidth: 240, padding: "9px 14px" }} placeholder="Search reports…" value={search} onChange={e => setSearch(e.target.value)} />
                  <div style={{ display: "flex", gap: 6 }}>
                    {cats.map(c => (
                      <button key={c} onClick={() => setCat(c)} style={{ padding: "7px 14px", borderRadius: 20, border: `1px solid ${cat === c ? T.accent : T.border}`, background: cat === c ? T.accentGlow : "transparent", color: cat === c ? T.accent : T.textSecondary, cursor: "pointer", fontSize: 12, fontWeight: 500, fontFamily: "'Plus Jakarta Sans', sans-serif", transition: "all .15s" }}>{c}</button>
                    ))}
                  </div>
                </div>

                {/* Report grid */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
                  {filtered.map((r, i) => (
                    <div key={r.id} className="report-card" style={{ animationDelay: `${.1 + i * .06}s` }} onClick={() => openReport(r)}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                        {r.cat && <span className="badge" style={{ background: `${CAT_COLOR[r.cat] || T.accent}18`, color: CAT_COLOR[r.cat] || T.accent, border: `1px solid ${CAT_COLOR[r.cat] || T.accent}30`, fontSize: 10 }}>{r.cat}</span>}
                        {r.updated && <span style={{ fontSize: 11, color: T.textMuted }}>{r.updated}</span>}
                      </div>
                      <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>{r.name}</h3>
                      <p style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.55 }}>{r.description}</p>
                      <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 5, color: T.accent, fontSize: 12, fontWeight: 500 }}>
                        <Icon n="external" size={13} /> Open report
                      </div>
                    </div>
                  ))}
                  {filtered.length === 0 && !loadingReports && (
                    <div style={{ gridColumn: "1/-1", textAlign: "center", padding: 48, color: T.textMuted }}>
                      <Icon n="chart" size={32} color={T.border} />
                      <p style={{ marginTop: 12, fontSize: 14 }}>No reports found</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Power BI Report Viewer ── */}
        {nav === "reports" && activeReport && (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "24px 32px", gap: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <button className="btn-secondary" style={{ padding: "8px 14px" }} onClick={() => { setActiveReport(null); setEmbedData(null); }}>← Back</button>
              <div style={{ flex: 1 }}>
                <h2 style={{ fontSize: 18, fontWeight: 600 }}>{activeReport.name}</h2>
                <p style={{ fontSize: 12, color: T.textSecondary }}>{activeReport.description}</p>
              </div>
              <span className="badge badge-success"><span style={{ width: 5, height: 5, borderRadius: "50%", background: T.success }} />Live data</span>
              <button
                onClick={async () => {
                  if (pbiReportRef.current?.print) {
                    try { await pbiReportRef.current.print(); } catch { window.print(); }
                  } else { window.print(); }
                }}
                style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: `1px solid ${T.border}`, borderRadius: 6, padding: "6px 12px", color: T.textSecondary, cursor: "pointer", fontSize: 12, fontFamily: "'Plus Jakarta Sans', sans-serif", whiteSpace: "nowrap" }}>
                ↓ PDF
              </button>

              {/* Share by email */}
              <button
                onClick={() => { setShowShareModal(true); setShareEmail(""); setShareNote(""); }}
                style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: `1px solid ${T.border}`, borderRadius: 6, padding: "6px 12px", color: T.textSecondary, cursor: "pointer", fontSize: 12, fontFamily: "'Plus Jakarta Sans', sans-serif", whiteSpace: "nowrap" }}>
                ✉ Share
              </button>
            </div>

            {/* Share modal */}
            {showShareModal && (
              <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
                <div className="card" style={{ width: 440, padding: 28 }}>
                  <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>Share report</h2>
                  <p style={{ fontSize: 13, color: T.textSecondary, marginBottom: 20 }}>{activeReport?.name}</p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <div className="field-group">
                      <label className="field-label">Recipient email</label>
                      <input className="field-input" type="email" placeholder="colleague@company.com" value={shareEmail} onChange={e => setShareEmail(e.target.value)} />
                    </div>
                    <div className="field-group">
                      <label className="field-label">Personal note (optional)</label>
                      <textarea className="field-input" rows={3} placeholder="Here's the report you asked about..." value={shareNote} onChange={e => setShareNote(e.target.value)} style={{ resize: "vertical", fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 13 }} />
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
                    <button
                      className="btn-primary"
                      disabled={shareSending || !shareEmail.trim()}
                      onClick={async () => {
                        setShareSending(true);
                        try {
                          const res = await fetch(`${API_BASE}/email-report`, {
                            method: "POST",
                            headers: { ...authHeaders(token, viewAs?.id), "Content-Type": "application/json" },
                            body: JSON.stringify({
                              reportId: activeReport?.pbiReportId || activeReport?.id,
                              reportName: activeReport?.name,
                              reportDescription: activeReport?.description,
                              recipientEmail: shareEmail.trim(),
                              note: shareNote.trim(),
                            }),
                          });
                          if (res.ok) {
                            setShowShareModal(false);
                            alert(`Report shared with ${shareEmail} ✓`);
                          } else {
                            const e = await res.json();
                            alert("Failed: " + (e.error || "Unknown error"));
                          }
                        } catch { alert("Send failed. Please try again."); }
                        finally { setShareSending(false); }
                      }}
                      style={{ flex: 1 }}>
                      {shareSending ? "Sending…" : "Send email"}
                    </button>
                    <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setShowShareModal(false)}>Cancel</button>
                  </div>
                </div>
              </div>
            )}

            <div style={{ flex: 1, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden", background: T.surface, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 500 }}>

              {loadingEmbed && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14, color: T.textSecondary }}>
                  <span className="spinner spin" style={{ width: 28, height: 28, borderTopColor: T.accent, borderColor: `rgba(196,122,16,.3)` }} />
                  <p style={{ fontSize: 14 }}>Loading report…</p>
                </div>
              )}

              {embedErr && (
                <div style={{ textAlign: "center", padding: 32 }}>
                  <div className="alert alert-error" style={{ display: "inline-flex" }}><Icon n="alert" size={15} color={T.error} />{embedErr}</div>
                  <br /><br />
                  <button className="btn-secondary" onClick={() => openReport(activeReport)}>Retry</button>
                </div>
              )}

              {/* Power BI Embedded iframe — uses the token from /api/embed-token */}
             {embedData && !loadingEmbed && (
  <div
    id="report-print-frame"
    ref={el => {
      if (el && embedData && window.powerbi) {
        window.powerbi.reset(el);
        pbiReportRef.current = window.powerbi.embed(el, {
          type: 'report',
          id: embedData.reportId,
          embedUrl: embedData.embedUrl,
          accessToken: embedData.token,
          tokenType: window.models?.TokenType?.Embed ?? 1,
          settings: {
            navContentPaneEnabled: true,
            filterPaneEnabled: true,
            exportDataEnabled: true,
          }
        });
      }
    }}
    style={{ width: "100%", height: "100%", minHeight: 600 }}
  />
)}
            </div>

            {/* Comments panel */}
            <div style={{ borderTop: `1px solid ${T.border}`, padding: 16 }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: T.textSecondary, marginBottom: 10 }}>Comments ({reportComments.length})</p>
              <div style={{ maxHeight: 180, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8, marginBottom: 10 }}>
                {reportComments.map(c => (
                  <div key={c.id} style={{ background: T.bg, borderRadius: 6, padding: "8px 10px", fontSize: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                      <span style={{ fontWeight: 600, color: T.accent }}>{c.user?.name || c.user?.email}</span>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <span style={{ color: T.textMuted, fontSize: 11 }}>{new Date(c.createdAt).toLocaleDateString()}</span>
                        <button onClick={async () => { await fetch(`${API_BASE}/comments`, { method: "DELETE", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify({ id: c.id }) }); loadComments(activeReport.pbiReportId || activeReport.id); }} style={{ background: "none", border: "none", cursor: "pointer", color: T.textMuted, padding: 0 }}><Icon n="x" size={11} /></button>
                      </div>
                    </div>
                    <p style={{ color: T.textPrimary, lineHeight: 1.5 }}>{c.content}</p>
                  </div>
                ))}
                {reportComments.length === 0 && <p style={{ fontSize: 11, color: T.textMuted }}>No comments yet — be the first to add one</p>}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <input className="field-input" style={{ fontSize: 12 }} placeholder="Add a comment…" value={newComment} onChange={e => setNewComment(e.target.value)} onKeyDown={async e => { if (e.key === "Enter" && newComment.trim()) { await fetch(`${API_BASE}/comments`, { method: "POST", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify({ pbiReportId: activeReport.pbiReportId || activeReport.id, reportName: activeReport.name, content: newComment.trim() }) }); setNewComment(""); loadComments(activeReport.pbiReportId || activeReport.id); }}} />
                <button className="btn-secondary" style={{ whiteSpace: "nowrap", padding: "8px 14px" }} onClick={async () => { if (!newComment.trim()) return; await fetch(`${API_BASE}/comments`, { method: "POST", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify({ pbiReportId: activeReport.pbiReportId || activeReport.id, reportName: activeReport.name, content: newComment.trim() }) }); setNewComment(""); loadComments(activeReport.pbiReportId || activeReport.id); }}>Post</button>
              </div>
            </div>
          </div>
        )}
{/* ── Alternative Reports ── */}
{nav === "alt-reports" && !altReport && (
  <div style={{ flex: 1, overflow: "auto", padding: "32px" }}>
    <div className="fu" style={{ marginBottom: 28 }}>
      <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Alternative Reports</h1>
      <p style={{ fontSize: 13, color: T.textSecondary }}>Interactive Plotly reports — click any report to open it</p>
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
      {altReportsForTenant.map((r, i) => (
        <div key={r.id} className="report-card" style={{ animationDelay: `${.1 + i * .06}s` }} onClick={() => { setAltReport(r); if (r.type === "analytics") { setAnalyticsData(null); setAnalyticsLoading(true); setAnalyticsErr(""); fetch(`${API_BASE}/analytics`, { headers: authHeaders(token, viewAs?.id) }).then(res=>res.json()).then(d=>{ if(d.error) throw new Error(d.error); setAnalyticsData(d); }).catch(e=>setAnalyticsErr(e.message)).finally(()=>setAnalyticsLoading(false)); } }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
            <span className="badge badge-info">{r.tenant}</span>
          </div>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>{r.name}</h3>
          <p style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.55 }}>{r.description}</p>
          <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 5, color: T.accent, fontSize: 12, fontWeight: 500 }}>
            <Icon n="external" size={13} /> Open report
          </div>
        </div>
      ))}
    </div>
  </div>
)}
{nav === "alt-reports" && altReport && (
  <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "24px 32px", gap: 16, overflow: altReport.type === "analytics" ? "auto" : "hidden" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
      <button className="btn-secondary" style={{ padding: "8px 14px" }} onClick={() => { setAltReport(null); setAnalyticsData(null); }}>← Back</button>
      <div style={{ flex: 1 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600 }}>{altReport.name}</h2>
        <p style={{ fontSize: 12, color: T.textSecondary }}>{altReport.description}</p>
      </div>
      <span className="badge badge-info">{altReport.tenant}</span>
      {altReport.type === "analytics" && (
        <button onClick={() => { setAnalyticsData(null); setAnalyticsLoading(true); setAnalyticsErr(""); fetch(`${API_BASE}/analytics`, { headers: authHeaders(token, viewAs?.id) }).then(r=>r.json()).then(d=>{ if(d.error) throw new Error(d.error); setAnalyticsData(d); }).catch(e=>setAnalyticsErr(e.message)).finally(()=>setAnalyticsLoading(false)); }}
          style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 8, padding: "7px 14px", color: T.textSecondary, cursor: "pointer", fontSize: 12, fontFamily: "'Plus Jakarta Sans',sans-serif", display: "flex", alignItems: "center", gap: 6 }}>
          <Icon n="refresh" size={13} />Refresh
        </button>
      )}
    </div>
    {altReport.type === "html" && (
      <iframe src={altReport.file} style={{ flex: 1, border: "none", borderRadius: 8, background: "#fff", height: "calc(100vh - 120px)", width: "100%" }} title={altReport.name} />
    )}
    {altReport.type === "analytics" && analyticsLoading && (
      <div style={{ display: "flex", alignItems: "center", gap: 12, color: T.textSecondary, padding: 48 }}>
        <span className="spinner spin" style={{ width: 20, height: 20, borderTopColor: T.accent, borderColor: `rgba(196,122,16,.3)` }} />Loading analytics…
      </div>
    )}
    {altReport.type === "analytics" && analyticsErr && <div className="alert alert-error"><Icon n="alert" size={15} color={T.error} />{analyticsErr}</div>}
    {altReport.type === "analytics" && analyticsData && <AnalyticsCharts data={analyticsData} />}
  </div>
)}
{/* ── Order Lookup ── */}
{nav === "order-lookup" && (
  <div style={{ flex: 1, overflow: "auto", padding: "32px 36px" }}>
    <div className="fu" style={{ marginBottom: 24 }}>
      <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Order Lookup</h1>
      <p style={{ fontSize: 13, color: T.textSecondary }}>Enter a sales order number to see everything related — items, invoices, shipments, videos & documents</p>
    </div>

    {/* Search */}
    <div style={{ display: "flex", gap: 10, marginBottom: 28, maxWidth: 500 }}>
      <input className="field-input" placeholder="e.g. 1968514" value={soQuery}
        onChange={e => setSoQuery(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter") {
          setSoData(null); setSoErr(""); setSoLoading(true);
          fetch(`${API_BASE}/order-lookup?soNo=${encodeURIComponent(soQuery.trim())}`, { headers: authHeaders(token, viewAs?.id) })
            .then(r => r.json()).then(d => { if (d.error) throw new Error(d.error); setSoData(d); })
            .catch(e => setSoErr(e.message)).finally(() => setSoLoading(false));
        }}}
        style={{ flex: 1, fontSize: 16, fontFamily: "'JetBrains Mono',monospace" }} />
      <button className="btn-primary" style={{ width: "auto", padding: "11px 24px" }}
        disabled={soLoading || !soQuery.trim()}
        onClick={() => {
          setSoData(null); setSoErr(""); setSoLoading(true);
          fetch(`${API_BASE}/order-lookup?soNo=${encodeURIComponent(soQuery.trim())}`, { headers: authHeaders(token, viewAs?.id) })
            .then(r => r.json()).then(d => { if (d.error) throw new Error(d.error); setSoData(d); })
            .catch(e => setSoErr(e.message)).finally(() => setSoLoading(false));
        }}>
        {soLoading ? <><span className="spinner spin" style={{ borderTopColor: "#fff", borderColor: "rgba(255,255,255,.3)" }} /> Searching…</> : "Search"}
      </button>
    </div>

    {soErr && <div className="alert alert-error" style={{ marginBottom: 20 }}><Icon n="alert" size={15} color={T.error} />{soErr}</div>}

    {soData && <SalesOrderDetail data={soData} />}

    {!soData && !soLoading && !soErr && (
      <div style={{ textAlign: "center", padding: "64px 0", color: T.textMuted }}>
        <Icon n="db" size={40} color={T.border} />
        <p style={{ marginTop: 16, fontSize: 14 }}>Enter a sales order number above</p>
        <p style={{ fontSize: 12, marginTop: 6 }}>Shows items, invoices, shipment status, YouTube videos & documents</p>
      </div>
    )}
  </div>
)}
{/* ── AI Agent ── */}
{nav === "ai" && (
  <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "32px" }}>
    <div className="fu" style={{ marginBottom: 20, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>AI Data Agent</h1>
        <p style={{ fontSize: 13, color: T.textSecondary }}>Ask any question about your business data in plain English</p>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={() => { setShowHistory(!showHistory); }} style={{ background: showHistory ? T.accentGlow2 : "none", border: `1px solid ${T.border}`, borderRadius: 6, padding: "6px 12px", color: showHistory ? T.accent : T.textSecondary, cursor: "pointer", fontSize: 12, fontFamily: "'Plus Jakarta Sans', sans-serif", display: "flex", alignItems: "center", gap: 5 }}>
          <Icon n="clock" size={13} /> History
        </button>
        <button onClick={async () => { await fetch(`${API_BASE}/chat-history`, { method: "DELETE", headers: authHeaders(token, viewAs?.id) }); setMessages([]); setChatHistory([]); }} style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 6, padding: "6px 12px", color: T.textMuted, cursor: "pointer", fontSize: 12, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Clear
        </button>
      </div>
    </div>

    <div className="card" style={{ flex: 1, display: "flex", flexDirection: "column", maxHeight: "70vh" }}>
      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px", display: "flex", flexDirection: "column", gap: 12 }} id="chat-messages">
        {messages.length === 0 && (
          <div style={{ textAlign: "center", padding: 48, color: T.textMuted }}>
            <Icon n="zap" size={32} color={T.border} />
            <p style={{ marginTop: 12, fontSize: 14 }}>Ask me anything about your business data</p>
            <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 8 }}>
              {["What were my top 5 customers last month?", "Show me overdue invoices", "How is my revenue trending?"].map(q => (
                <button key={q} onClick={() => sendMessage(q)} style={{ background: T.accentGlow2, border: `1px solid rgba(196,122,16,.2)`, borderRadius: 8, padding: "8px 16px", color: T.accent, cursor: "pointer", fontSize: 13, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{q}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
            <div style={{ maxWidth: m.chart ? "98%" : "75%", width: m.chart ? "98%" : undefined, padding: "12px 16px", borderRadius: 12, background: m.role === "user" ? T.accent : T.surface, border: `1px solid ${m.role === "user" ? T.accent : T.border}`, color: m.role === "user" ? "#fff" : T.textPrimary, fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
              {m.content}
              {m.chart && <ChartWidget chart={m.chart} />}
              {m.exportData && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.border}`, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button onClick={() => downloadExcel(m.exportData, m.question)} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(34,197,94,.12)", border: "1px solid rgba(34,197,94,.25)", borderRadius: 6, padding: "5px 12px", color: "#22c55e", cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    ↓ Export to Excel ({m.exportData.length.toLocaleString()} rows)
                  </button>
                  {m.role === "assistant" && m.question && (
                    <button onClick={async () => {
                      const name = prompt("Save as KPI card — enter a short name:", m.question.slice(0,40));
                      if (!name) return;
                      await fetch(`${API_BASE}/saved-queries`, { method: "POST", headers: { ...authHeaders(token, viewAs?.id), "Content-Type": "application/json" }, body: JSON.stringify({ name, question: m.question }) });
                      loadSavedQueries();
                      alert("Saved to Home dashboard ✓");
                    }} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: T.accentGlow2, border: `1px solid rgba(196,122,16,.25)`, borderRadius: 6, padding: "5px 12px", color: T.accent, cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                      ☆ Save as KPI
                    </button>
                  )}
                </div>
              )}
              {m.role === "assistant" && m.question && !m.exportData && (
                <div style={{ marginTop: 8 }}>
                  <button onClick={async () => {
                    const name = prompt("Save as KPI card:", m.question.slice(0,40));
                    if (!name) return;
                    await fetch(`${API_BASE}/saved-queries`, { method: "POST", headers: { ...authHeaders(token, viewAs?.id), "Content-Type": "application/json" }, body: JSON.stringify({ name, question: m.question }) });
                    loadSavedQueries(); alert("Saved to Home dashboard ✓");
                  }} style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif", padding: 0 }}>
                    ☆ Save as KPI
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
        {aiLoading && (
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div style={{ padding: "12px 16px", borderRadius: 12, background: T.surface, border: `1px solid ${T.border}` }}>
              <span className="spinner spin" style={{ width: 16, height: 16, borderTopColor: T.accent, borderColor: `rgba(196,122,16,.3)` }} />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Saved queries quick-access */}
      {savedQueries.length > 0 && (
        <div style={{ borderTop: `1px solid ${T.border}`, padding: "8px 16px", display: "flex", gap: 6, overflowX: "auto" }}>
          {savedQueries.slice(0,5).map(q => (
            <button key={q.id} onClick={() => sendMessage(q.question)} style={{ background: T.accentGlow2, border: `1px solid rgba(196,122,16,.2)`, borderRadius: 20, padding: "4px 12px", color: T.accent, cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif", whiteSpace: "nowrap", flexShrink: 0 }}>
              {q.name}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div style={{ borderTop: `1px solid ${T.border}`, padding: 16, display: "flex", gap: 10 }}>
        <input className="field-input" placeholder="Ask anything about your data..." value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage()} disabled={aiLoading} />
        <button type="button" className="btn-primary" style={{ width: "auto", padding: "11px 20px", whiteSpace: "nowrap" }} onClick={() => sendMessage()} disabled={aiLoading || !input.trim()}>Send</button>
      </div>
    </div>

    {/* History sidebar */}
    {showHistory && (
      <div style={{ width: 280, background: T.surface, borderLeft: `1px solid ${T.border}`, display: "flex", flexDirection: "column", marginTop: -60 }}>
        <div style={{ padding: "16px", borderBottom: `1px solid ${T.border}` }}>
          <p style={{ fontSize: 13, fontWeight: 600 }}>Chat history</p>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 12 }}>
          {chatHistory.filter(m => m.role === "user").map(m => (
            <div key={m.id} onClick={() => sendMessage(m.content)} style={{ padding: "8px 10px", borderRadius: 6, cursor: "pointer", marginBottom: 4, fontSize: 12, color: T.textSecondary, background: T.bg, border: `1px solid ${T.border}` }}
              onMouseEnter={e => e.currentTarget.style.background = T.surfaceHover}
              onMouseLeave={e => e.currentTarget.style.background = T.bg}>
              {m.content.slice(0, 60)}{m.content.length > 60 ? "…" : ""}
            </div>
          ))}
          {chatHistory.filter(m => m.role === "user").length === 0 && <p style={{ fontSize: 12, color: T.textMuted, textAlign: "center", padding: 20 }}>No history yet</p>}
        </div>
      </div>
    )}
  </div>
)}
        {/* ── Alerts ── */}
        {nav === "alerts" && (
          <div style={{ flex: 1, overflow: "auto", padding: "32px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
              <div><h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>KPI Alerts</h1><p style={{ fontSize: 13, color: T.textSecondary }}>Get emailed when a metric crosses a threshold</p></div>
              <button className="btn-primary" style={{ width: "auto", padding: "8px 16px", fontSize: 12 }} onClick={() => setShowAlertModal(true)}>+ New Alert</button>
            </div>
            {alerts.length === 0 ? (
              <div style={{ textAlign: "center", padding: 64, color: T.textMuted }}><Icon n="bell" size={32} color={T.border} /><p style={{ marginTop: 12 }}>No alerts set up yet</p></div>
            ) : alerts.map(a => (
              <div key={a.id} className="card" style={{ padding: 16, marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <p style={{ fontWeight: 600, marginBottom: 4 }}>{a.name}</p>
                  <p style={{ fontSize: 12, color: T.textSecondary }}>{a.question}</p>
                  <p style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>Threshold: {a.condition === "gt" ? ">" : a.condition === "lt" ? "<" : "="} {a.threshold} · {a.frequency} · → {a.notifyEmail}{a.lastValue != null ? ` · Last: ${a.lastValue}` : ""}</p>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={async () => { await fetch(`${API_BASE}/alerts`, { method: "PATCH", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify({ id: a.id, isActive: !a.isActive }) }); loadAlerts(); }} style={{ background: a.isActive ? "rgba(34,197,94,.12)" : T.bg, border: `1px solid ${a.isActive ? "rgba(34,197,94,.3)" : T.border}`, borderRadius: 6, padding: "4px 10px", color: a.isActive ? "#22c55e" : T.textMuted, cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{a.isActive ? "Active" : "Paused"}</button>
                  <button onClick={async () => { await fetch(`${API_BASE}/alerts`, { method: "DELETE", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify({ id: a.id }) }); loadAlerts(); }} style={{ background: "none", border: `1px solid rgba(239,68,68,.3)`, borderRadius: 6, padding: "4px 10px", color: "#ef4444", cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Delete</button>
                </div>
              </div>
            ))}
            {showAlertModal && (
              <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
                <div className="card" style={{ width: 480, padding: 28 }}>
                  <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 20 }}>New KPI Alert</h2>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <input className="field-input" placeholder="Alert name (e.g. High open orders)" value={newAlert.name} onChange={e => setNewAlert(p => ({ ...p, name: e.target.value }))} />
                    <input className="field-input" placeholder="AI question (e.g. how many open sales orders)" value={newAlert.question} onChange={e => setNewAlert(p => ({ ...p, question: e.target.value }))} />
                    <div style={{ display: "flex", gap: 8 }}>
                      <select className="field-input" value={newAlert.condition} onChange={e => setNewAlert(p => ({ ...p, condition: e.target.value }))} style={{ background: T.surface, color: T.textPrimary }}>
                        <option value="gt">Greater than (&gt;)</option><option value="lt">Less than (&lt;)</option><option value="eq">Equal to (=)</option>
                      </select>
                      <input className="field-input" placeholder="Threshold (e.g. 500)" type="number" value={newAlert.threshold} onChange={e => setNewAlert(p => ({ ...p, threshold: e.target.value }))} />
                    </div>
                    <input className="field-input" placeholder="Notify email" type="email" value={newAlert.notifyEmail} onChange={e => setNewAlert(p => ({ ...p, notifyEmail: e.target.value }))} />
                    <select className="field-input" value={newAlert.frequency} onChange={e => setNewAlert(p => ({ ...p, frequency: e.target.value }))} style={{ background: T.surface, color: T.textPrimary }}>
                      <option value="hourly">Check hourly</option><option value="daily">Check daily</option><option value="weekly">Check weekly</option>
                    </select>
                  </div>
                  <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
                    <button className="btn-primary" style={{ flex: 1 }} onClick={async () => { await fetch(`${API_BASE}/alerts`, { method: "POST", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify({ ...newAlert, threshold: Number(newAlert.threshold) }) }); setShowAlertModal(false); setNewAlert({ name:"", question:"", condition:"gt", threshold:"", notifyEmail:"", frequency:"daily" }); loadAlerts(); }}>Create Alert</button>
                    <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setShowAlertModal(false)}>Cancel</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Scheduled Reports ── */}
        {nav === "schedule" && (
          <div style={{ flex: 1, overflow: "auto", padding: "32px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
              <div><h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Scheduled Reports</h1><p style={{ fontSize: 13, color: T.textSecondary }}>Automatically email reports or AI summaries on a schedule</p></div>
              <button className="btn-primary" style={{ width: "auto", padding: "8px 16px", fontSize: 12 }} onClick={() => setShowScheduleModal(true)}>+ New Schedule</button>
            </div>
            {schedules.length === 0 ? (
              <div style={{ textAlign: "center", padding: 64, color: T.textMuted }}><Icon n="clock" size={32} color={T.border} /><p style={{ marginTop: 12 }}>No scheduled reports yet</p></div>
            ) : schedules.map(s => (
              <div key={s.id} className="card" style={{ padding: 16, marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <p style={{ fontWeight: 600, marginBottom: 4 }}>{s.reportName}</p>
                  <p style={{ fontSize: 12, color: T.textSecondary }}>{s.question || "Power BI report"}</p>
                  <p style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>{s.frequency} · {s.recipients} {s.nextRun ? `· Next: ${new Date(s.nextRun).toLocaleDateString()}` : ""}</p>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={async () => { await fetch(`${API_BASE}/scheduled-reports`, { method: "PATCH", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify({ id: s.id, isActive: !s.isActive }) }); loadSchedules(); }} style={{ background: s.isActive ? "rgba(34,197,94,.12)" : T.bg, border: `1px solid ${s.isActive ? "rgba(34,197,94,.3)" : T.border}`, borderRadius: 6, padding: "4px 10px", color: s.isActive ? "#22c55e" : T.textMuted, cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{s.isActive ? "Active" : "Paused"}</button>
                  <button onClick={async () => { await fetch(`${API_BASE}/scheduled-reports`, { method: "DELETE", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify({ id: s.id }) }); loadSchedules(); }} style={{ background: "none", border: `1px solid rgba(239,68,68,.3)`, borderRadius: 6, padding: "4px 10px", color: "#ef4444", cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Delete</button>
                </div>
              </div>
            ))}
            {showScheduleModal && (
              <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
                <div className="card" style={{ width: 480, padding: 28 }}>
                  <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 20 }}>New Scheduled Report</h2>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <input className="field-input" placeholder="Report name" value={newSchedule.reportName} onChange={e => setNewSchedule(p => ({ ...p, reportName: e.target.value }))} />
                    <input className="field-input" placeholder="AI question for summary (optional)" value={newSchedule.question} onChange={e => setNewSchedule(p => ({ ...p, question: e.target.value }))} />
                    <input className="field-input" placeholder="Recipients (comma-separated emails)" value={newSchedule.recipients} onChange={e => setNewSchedule(p => ({ ...p, recipients: e.target.value }))} />
                    <div style={{ display: "flex", gap: 8 }}>
                      <select className="field-input" value={newSchedule.frequency} onChange={e => setNewSchedule(p => ({ ...p, frequency: e.target.value }))} style={{ background: T.surface, color: T.textPrimary }}>
                        <option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option>
                      </select>
                      <select className="field-input" value={newSchedule.hour} onChange={e => setNewSchedule(p => ({ ...p, hour: Number(e.target.value) }))} style={{ background: T.surface, color: T.textPrimary }}>
                        {[6,7,8,9,10,11,12,13,14,15,16,17,18].map(h => <option key={h} value={h}>{h}:00</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
                    <button className="btn-primary" style={{ flex: 1 }} onClick={async () => { await fetch(`${API_BASE}/scheduled-reports`, { method: "POST", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify(newSchedule) }); setShowScheduleModal(false); setNewSchedule({ reportName:"", question:"", recipients:"", frequency:"weekly", dayOfWeek:1, hour:8 }); loadSchedules(); }}>Create Schedule</button>
                    <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setShowScheduleModal(false)}>Cancel</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Audit Log ── */}
        {nav === "audit" && (
          <div style={{ flex: 1, overflow: "auto", padding: "32px" }}>
            <div style={{ marginBottom: 28 }}>
              <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Audit Log</h1>
              <p style={{ fontSize: 13, color: T.textSecondary }}>Track who viewed what and when</p>
            </div>
            <div className="card" style={{ padding: 0 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                    {["User","Action","Detail","When"].map(h => <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontSize: 11, fontWeight: 600, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map(l => (
                    <tr key={l.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                      <td style={{ padding: "10px 16px", fontSize: 13 }}>{l.user?.name || l.user?.email}</td>
                      <td style={{ padding: "10px 16px", fontSize: 12 }}><span style={{ background: T.accentGlow2, border: `1px solid rgba(196,122,16,.2)`, color: T.accent, padding: "2px 8px", borderRadius: 12, fontSize: 11 }}>{l.action}</span></td>
                      <td style={{ padding: "10px 16px", fontSize: 12, color: T.textSecondary, maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.detail}</td>
                      <td style={{ padding: "10px 16px", fontSize: 11, color: T.textMuted, whiteSpace: "nowrap" }}>{new Date(l.createdAt).toLocaleString()}</td>
                    </tr>
                  ))}
                  {auditLogs.length === 0 && <tr><td colSpan={4} style={{ padding: 32, textAlign: "center", color: T.textMuted, fontSize: 13 }}>No activity logged yet</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

{/* ── Admin Panel ── */}
{nav === "admin" && (
  <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "32px" }}>
    <div style={{ marginBottom: 28 }}>
      <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Admin Panel</h1>
      <p style={{ fontSize: 13, color: T.textSecondary }}>Manage clients, reports and users</p>
    </div>

    {/* Stats */}
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 28 }}>
      {[
        { label: "Total Clients", value: adminData?.tenants?.length ?? "...", icon: "users" },
        { label: "Total Reports", value: adminData?.reports?.length ?? "...", icon: "grid" },
        { label: "Platform Status", value: "Live", icon: "zap" },
      ].map(s => (
        <div key={s.label} className="card" style={{ padding: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <Icon n={s.icon} size={16} color={T.accent} />
            <span style={{ fontSize: 12, color: T.textSecondary }}>{s.label}</span>
          </div>
          <div style={{ fontSize: 24, fontWeight: 700, color: T.textPrimary }}>{s.value}</div>
        </div>
      ))}
    </div>

{/* Clients list */}
    <div className="card" style={{ padding: 20, marginBottom: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600 }}>Clients</h2>
        <button className="btn-primary" style={{ width: "auto", padding: "8px 16px", fontSize: 12 }} onClick={() => setShowAddTenant(true)}>
          + Add Client
        </button>
      </div>
      {adminData?.tenants?.map(t => (
        <div key={t.id} style={{ borderBottom: `1px solid ${T.border}`, paddingBottom: 16, marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0" }}>
            <div>
              <p style={{ fontSize: 14, fontWeight: 600 }}>{t.name}</p>
              <p style={{ fontSize: 12, color: T.textSecondary }}>{t.slug} · {t.dbType === "client" ? "🔗 Client DB" : "🏠 Hosted DB"}</p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: t.isActive ? "rgba(34,197,94,.15)" : "rgba(239,68,68,.15)", color: t.isActive ? "#22c55e" : "#ef4444" }}>
                {t.isActive ? "Active" : "Inactive"}
              </span>
              <button onClick={() => setExpandedTenant(expandedTenant === t.id ? null : t.id)} style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 6, padding: "4px 10px", color: T.textSecondary, cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {expandedTenant === t.id ? "Hide" : "Manage"}
              </button>
              <button
                onClick={() => { setViewAs({ id: t.id, name: t.name }); setNav("reports"); setMessages([]); setActiveReport(null); }}
                style={{ background: T.accentGlow2, border: `1px solid rgba(196,122,16,.3)`, borderRadius: 6, padding: "4px 10px", color: T.accent, cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                👁 View as
              </button>
              <button
                onClick={async () => {
                  const next = !t.isActive;
                  await fetch(`${API_BASE}/admin/tenants/${t.id}`, { method: "PATCH", headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, "Content-Type": "application/json" }, body: JSON.stringify({ isActive: next }) });
                  loadAdminData();
                }}
                style={{ background: "none", border: `1px solid ${t.isActive ? "rgba(239,68,68,.3)" : "rgba(34,197,94,.3)"}`, borderRadius: 6, padding: "4px 10px", color: t.isActive ? "#ef4444" : "#22c55e", cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {t.isActive ? "Disable" : "Enable"}
              </button>
              <button
                onClick={async () => {
                  if (!window.confirm(`Permanently delete "${t.name}" and all their users? This cannot be undone.`)) return;
                  await fetch(`${API_BASE}/admin/tenants/${t.id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}` } });
                  loadAdminData();
                }}
                style={{ background: "none", border: "1px solid rgba(239,68,68,.3)", borderRadius: 6, padding: "4px 10px", color: "#ef4444", cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Delete
              </button>
            </div>
          </div>

          {expandedTenant === t.id && (
            <div style={{ background: T.bg, borderRadius: 8, padding: 16, marginTop: 8 }}>

              {/* AI Context Section */}
              <div style={{ marginBottom: 20 }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 8 }}>AI Agent — Application & Context</p>

                {/* App type selector */}
                <div style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 10 }}>
                  <select
                    id={`app-type-${t.id}`}
                    defaultValue={t.appType || "unknown"}
                    className="field-input"
                    style={{ maxWidth: 220, background: T.surface, color: T.textPrimary }}>
                    <option value="unknown">Unknown / Auto-detect</option>
                    <option value="sage100">Sage 100 (MAS 90/200)</option>
                    <option value="sage50">Sage 50</option>
                    <option value="sapb1">SAP Business One</option>
                    <option value="dynamics365">Microsoft Dynamics 365 / BC</option>
                    <option value="quickbooks">QuickBooks</option>
                    <option value="netsuite">NetSuite</option>
                    <option value="odoo">Odoo</option>
                    <option value="syspro">SYSPRO</option>
                    <option value="custom">Custom / Other</option>
                  </select>
                  <button
                    onClick={async (e) => {
                      const btn = e.target;
                      btn.textContent = "Learning…";
                      btn.disabled = true;
                      try {
                        const appType = document.getElementById(`app-type-${t.id}`).value;
                        const res = await fetch(`${API_BASE}/admin/tenants/${t.id}/learn-schema`, {
                          method: "POST",
                          headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, "Content-Type": "application/json" },
                          body: JSON.stringify({ appType })
                        });
                        const data = await res.json();
                        if (!res.ok) { alert("Failed: " + data.error); return; }
                        document.getElementById(`ai-context-${t.id}`).value = data.aiContext;
                        loadAdminData();
                        alert(`✓ Schema learned! Analysed ${data.tableCount} tables. AI Context updated automatically.`);
                      } catch(err) {
                        alert("Error: " + err.message);
                      } finally {
                        btn.textContent = "🧠 Auto-learn schema";
                        btn.disabled = false;
                      }
                    }}
                    style={{ background: T.accentGlow2, border: `1px solid rgba(196,122,16,.3)`, borderRadius: 6, padding: "6px 14px", color: T.accent, cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif", whiteSpace: "nowrap" }}>
                    🧠 Auto-learn schema
                  </button>
                </div>

                <p style={{ fontSize: 11, color: T.textMuted, marginBottom: 8 }}>Auto-learn connects to the client's database, analyses the schema and generates the context below. You can edit it afterwards.</p>
                <textarea
                  className="field-input"
                  rows={5}
                  placeholder="Click Auto-learn to generate this automatically, or type manually..."
                  defaultValue={t.aiContext || ""}
                  id={`ai-context-${t.id}`}
                  style={{ width: "100%", resize: "vertical", fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 12 }}
                />
                <button
                  onClick={async () => {
                    const val = document.getElementById(`ai-context-${t.id}`).value;
                    const appType = document.getElementById(`app-type-${t.id}`).value;
                    await fetch(`${API_BASE}/admin/tenants/${t.id}`, {
                      method: "PATCH",
                      headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, "Content-Type": "application/json" },
                      body: JSON.stringify({ aiContext: val, appType })
                    });
                    loadAdminData();
                    alert("Saved ✓");
                  }}
                  style={{ marginTop: 8, background: T.accentGlow2, border: `1px solid rgba(196,122,16,.3)`, borderRadius: 6, padding: "5px 14px", color: T.accent, cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  Save
                </button>
              </div>

              <div style={{ height: 1, background: T.border, marginBottom: 16 }} />

              {/* KPI SQL Editor */}
              {(() => {
                const KPI_LABELS = [
                  { id: 'ytd-rev',      label: 'YTD Revenue',        hint: 'SUM of revenue for current year' },
                  { id: 'mtd-rev',      label: 'MTD Revenue',        hint: 'SUM of revenue for current month' },
                  { id: 'ytd-bud',      label: 'YTD Budget',         hint: 'Budget/target for current year (or SELECT \'No budget data\' AS result)' },
                  { id: 'mtd-bud',      label: 'MTD Budget',         hint: 'Budget/target for current month' },
                  { id: 'ytd-gp',       label: 'YTD GP%',            hint: 'Gross profit % for current year (e.g. 34.9)' },
                  { id: 'mtd-gp',       label: 'MTD GP%',            hint: 'Gross profit % for current month' },
                  { id: 'open-ord',     label: 'Open Orders (count)',  hint: 'COUNT of open confirmed orders' },
                  { id: 'open-ord-val', label: 'Open Order Balance',  hint: 'SUM value of open confirmed orders' },
                  { id: 'open-inv',     label: 'Open Invoices',       hint: 'SUM of outstanding invoice balances' },
                ];
                const kpiSql = t.kpiSql || {};
                return (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary }}>KPI SQL Queries</p>
                      <p style={{ fontSize: 11, color: T.textMuted }}>Write one SELECT query per KPI — must return a single value</p>
                    </div>
                    {KPI_LABELS.map(kpi => (
                      <div key={kpi.id} style={{ marginBottom: 12, background: T.bg, borderRadius: 8, padding: 12, border: `1px solid ${T.border}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                          <p style={{ fontSize: 11, fontWeight: 700, color: T.accent, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{kpi.label}</p>
                          <button
                            onClick={async () => {
                              const sql = document.getElementById(`kpi-sql-${t.id}-${kpi.id}`).value;
                              if (!sql.trim()) return;
                              const btn = document.getElementById(`kpi-test-${t.id}-${kpi.id}`);
                              btn.textContent = 'Testing…';
                              try {
                                const res = await fetch(`${API_BASE}/admin/tenants/${t.id}/test-sql`, {
                                  method: 'POST',
                                  headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ sql }),
                                });
                                const d = await res.json();
                                if (d.error) { btn.textContent = '✗ Error'; alert('SQL Error: ' + d.error); }
                                else { btn.textContent = '✓ OK: ' + JSON.stringify(Object.values(d.rows[0] || {})[0]); }
                              } catch(e) { btn.textContent = '✗ Failed'; }
                              setTimeout(() => { btn.textContent = 'Test'; }, 4000);
                            }}
                            id={`kpi-test-${t.id}-${kpi.id}`}
                            style={{ background: 'none', border: `1px solid ${T.border}`, borderRadius: 5, padding: '3px 10px', color: T.textSecondary, cursor: 'pointer', fontSize: 10, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                            Test
                          </button>
                        </div>
                        <p style={{ fontSize: 10, color: T.textMuted, marginBottom: 4 }}>{kpi.hint}</p>
                        <textarea
                          className="field-input"
                          id={`kpi-sql-${t.id}-${kpi.id}`}
                          rows={2}
                          defaultValue={kpiSql[kpi.id] || ''}
                          placeholder={`SELECT ... -- ${kpi.hint}`}
                          style={{ width: '100%', fontFamily: 'monospace', fontSize: 11, resize: 'vertical' }}
                        />
                      </div>
                    ))}
                    <button
                      onClick={async () => {
                        const newKpiSql = {};
                        KPI_LABELS.forEach(kpi => {
                          const val = document.getElementById(`kpi-sql-${t.id}-${kpi.id}`).value.trim();
                          if (val) newKpiSql[kpi.id] = val;
                        });
                        const res = await fetch(`${API_BASE}/admin/tenants/${t.id}`, {
                          method: 'PATCH',
                          headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, 'Content-Type': 'application/json' },
                          body: JSON.stringify({ kpiSql: newKpiSql }),
                        });
                        if (res.ok) { loadAdminData(); alert('KPI SQL saved ✓ — dashboard will use these queries immediately'); }
                        else alert('Save failed');
                      }}
                      style={{ background: T.accentGlow2, border: `1px solid rgba(196,122,16,.3)`, borderRadius: 6, padding: '6px 16px', color: T.accent, cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                      Save All KPI SQL
                    </button>
                  </div>
                );
              })()}

              <div style={{ height: 1, background: T.border, marginBottom: 16 }} />

              {/* Power BI Section */}
              <div style={{ marginBottom: 20 }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 8 }}>Power BI</p>
                <input
                  className="field-input"
                  placeholder="Workspace ID (GUID)"
                  defaultValue={t.pbiWorkspaceId || ""}
                  id={`pbi-ws-${t.id}`}
                  style={{ width: "100%", marginBottom: 8 }}
                />
                <input
                  className="field-input"
                  placeholder="Master user email (e.g. user@client.com)"
                  defaultValue={t.pbiMasterUser || ""}
                  id={`pbi-user-${t.id}`}
                  style={{ width: "100%", marginBottom: 8 }}
                />
                <input
                  className="field-input"
                  type="password"
                  placeholder="Master user password"
                  defaultValue={t.pbiMasterPassword || ""}
                  id={`pbi-pass-${t.id}`}
                  style={{ width: "100%", marginBottom: 8 }}
                />
                <input
                  className="field-input"
                  placeholder="Azure Tenant ID (GUID)"
                  defaultValue={t.azureTenantId || ""}
                  id={`pbi-az-${t.id}`}
                  style={{ width: "100%", marginBottom: 8 }}
                />
                <button
                  onClick={async () => {
                    const pbiWorkspaceId = document.getElementById(`pbi-ws-${t.id}`).value;
                    const pbiMasterUser = document.getElementById(`pbi-user-${t.id}`).value;
                    const pbiMasterPassword = document.getElementById(`pbi-pass-${t.id}`).value;
                    const azureTenantId = document.getElementById(`pbi-az-${t.id}`).value;
                    const res = await fetch(`${API_BASE}/admin/tenants/${t.id}`, {
                      method: "PATCH",
                      headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, "Content-Type": "application/json" },
                      body: JSON.stringify({ pbiWorkspaceId, pbiMasterUser, pbiMasterPassword, azureTenantId }),
                    });
                    if (res.ok) { loadAdminData(); alert("Saved ✓"); }
                    else alert("Save failed");
                  }}
                  style={{ background: T.accentGlow2, border: `1px solid rgba(196,122,16,.3)`, borderRadius: 6, padding: "5px 14px", color: T.accent, cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  Save Power BI
                </button>
              </div>

              <div style={{ height: 1, background: T.border, marginBottom: 16 }} />

              {/* Database Connection Section */}
              <div style={{ marginBottom: 20 }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 8 }}>Database Connection</p>
                <input
                  className="field-input"
                  placeholder="Connection string (postgresql://...)"
                  defaultValue={t.dbConnectionString || ""}
                  id={`db-conn-${t.id}`}
                  style={{ width: "100%", marginBottom: 8, fontFamily: "monospace", fontSize: 11 }}
                />
                <input
                  className="field-input"
                  placeholder="Schema (e.g. raindrop_snapshot)"
                  defaultValue={t.dbSchema || ""}
                  id={`db-schema-${t.id}`}
                  style={{ width: "100%", marginBottom: 8 }}
                />
                <button
                  onClick={async () => {
                    const dbConnectionString = document.getElementById(`db-conn-${t.id}`).value;
                    const dbSchema = document.getElementById(`db-schema-${t.id}`).value;
                    const res = await fetch(`${API_BASE}/admin/tenants/${t.id}`, {
                      method: "PATCH",
                      headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, "Content-Type": "application/json" },
                      body: JSON.stringify({ dbConnectionString, dbSchema }),
                    });
                    if (res.ok) { loadAdminData(); alert("Saved ✓"); }
                    else alert("Save failed");
                  }}
                  style={{ background: T.accentGlow2, border: `1px solid rgba(196,122,16,.3)`, borderRadius: 6, padding: "5px 14px", color: T.accent, cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  Save Connection
                </button>
              </div>

              <div style={{ height: 1, background: T.border, marginBottom: 16 }} />

              {/* Reports Section */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary }}>Reports</p>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {adminData?.reports?.map(r => {
                    const assigned = t.reports?.some(tr => tr.report?.id === r.id || tr.reportId === r.id)
                    return (
                      <div key={r.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 6, background: assigned ? "rgba(196,122,16,.08)" : "rgba(255,255,255,.03)", border: `1px solid ${assigned ? T.accent : T.border}` }}>
                        <div>
                          <p style={{ fontSize: 13, fontWeight: 500 }}>{r.name}</p>
                          <p style={{ fontSize: 11, color: T.textSecondary }}>{r.description || "No description"}</p>
                        </div>
                        <button
                          onClick={async () => {
                            try {
                              const res = await fetch(`${API_BASE}/admin/tenants/${t.id}/reports`, {
                                method: assigned ? "DELETE" : "POST",
                                headers: { Authorization: `Bearer ${token || sessionStorage.getItem("pvbi_token")}`, "Content-Type": "application/json" },
                                body: JSON.stringify({ reportId: r.id }),
                              })
                              if (res.ok) loadAdminData()
                              else alert("Failed to update report assignment")
                            } catch (err) {
                              alert("Error: " + err.message)
                            }
                          }}
                          style={{ background: assigned ? "rgba(239,68,68,.15)" : "rgba(196,122,16,.15)", border: "none", borderRadius: 6, padding: "4px 12px", color: assigned ? "#ef4444" : T.accent, cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                        >
                          {assigned ? "Remove" : "Assign"}
                        </button>
                      </div>
                    )
                  })}
                </div>
              </div>

              <div style={{ height: 1, background: T.border, marginBottom: 16 }} />

              {/* Users Section */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary }}>Users</p>
                <button onClick={() => { setShowAddUser(true); setAddUserTenantId(t.id); setAddUserTenantName(t.name); setNewUser({ name: "", email: "", role: "VIEWER" }); }} style={{ background: "none", border: `1px solid ${T.accent}`, borderRadius: 6, padding: "4px 10px", color: T.accent, cursor: "pointer", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  + Invite User
                </button>
              </div>
              {!t.users || t.users.length === 0 ? (
                <p style={{ fontSize: 12, color: T.textMuted }}>No users yet — invite your first user above.</p>
              ) : (
                t.users.map(u => (
                  <div key={u.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${T.border}` }}>
                    <div>
                      <p style={{ fontSize: 13, fontWeight: 500 }}>{u.name}</p>
                      <p style={{ fontSize: 11, color: T.textSecondary }}>{u.email}</p>
                    </div>
                    <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: "rgba(196,122,16,.15)", color: T.accent }}>{u.role}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      ))}
    </div>

    {/* Invite user modal — top level so it's never trapped by overflow/transform */}
    {showAddUser && (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
        <div className="card" style={{ width: 420, padding: 28 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>Invite User</h2>
          <p style={{ fontSize: 12, color: T.textSecondary, marginBottom: 20 }}>Adding to: {addUserTenantName}</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <input className="field-input" placeholder="Full name" value={newUser.name} onChange={e => setNewUser(p => ({ ...p, name: e.target.value }))} />
            <input className="field-input" placeholder="Email address" value={newUser.email} onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))} />
            <select className="field-input" value={newUser.role} onChange={e => setNewUser(p => ({ ...p, role: e.target.value }))} style={{ background: T.surface, color: T.textPrimary }}>
              <option value="VIEWER">Viewer</option>
              <option value="ADMIN">Admin</option>
            </select>
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            <button className="btn-primary" style={{ flex: 1 }} onClick={inviteUser} disabled={inviteLoading}>
              {inviteLoading ? "Sending invite..." : "Send Invitation"}
            </button>
            <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setShowAddUser(false)}>Cancel</button>
          </div>
        </div>
      </div>
    )}

    {/* Add tenant modal */}
    {showAddTenant && (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
        <div className="card" style={{ width: 440, padding: 28 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 20 }}>Add New Client</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <input className="field-input" placeholder="Company name (e.g. Acme Corp)" value={newTenant.name} onChange={e => setNewTenant(p => ({ ...p, name: e.target.value }))} />
            <input className="field-input" placeholder="Slug (e.g. acme-corp)" value={newTenant.slug} onChange={e => setNewTenant(p => ({ ...p, slug: e.target.value }))} />
            <div>
              <input className="field-input" placeholder="Azure Tenant ID (from Azure Portal → Overview)" value={newTenant.azureTenantId} onChange={e => setNewTenant(p => ({ ...p, azureTenantId: e.target.value }))} />
              <p style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>Azure Portal → Azure Active Directory → Overview → Tenant ID</p>
            </div>
            <input className="field-input" placeholder="Power BI Workspace ID (optional)" value={newTenant.pbiWorkspaceId} onChange={e => setNewTenant(p => ({ ...p, pbiWorkspaceId: e.target.value }))} />
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setNewTenant(p => ({ ...p, dbType: "hosted" }))} style={{ flex: 1, padding: "10px", borderRadius: 8, border: `1px solid ${newTenant.dbType === "hosted" ? T.accent : T.border}`, background: newTenant.dbType === "hosted" ? T.accentGlow2 : "transparent", color: newTenant.dbType === "hosted" ? T.accent : T.textSecondary, cursor: "pointer", fontSize: 12, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                🏠 We host (our Supabase)
              </button>
              <button onClick={() => setNewTenant(p => ({ ...p, dbType: "client" }))} style={{ flex: 1, padding: "10px", borderRadius: 8, border: `1px solid ${newTenant.dbType === "client" ? T.accent : T.border}`, background: newTenant.dbType === "client" ? T.accentGlow2 : "transparent", color: newTenant.dbType === "client" ? T.accent : T.textSecondary, cursor: "pointer", fontSize: 12, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                🔗 Client's own DB
              </button>
            </div>
            {newTenant.dbType === "client" && (
              <>
                <input className="field-input" placeholder="Connection string (postgresql://...)" value={newTenant.dbConnectionString} onChange={e => setNewTenant(p => ({ ...p, dbConnectionString: e.target.value }))} />
                <input className="field-input" placeholder="Schema name (e.g. public)" value={newTenant.dbSchema} onChange={e => setNewTenant(p => ({ ...p, dbSchema: e.target.value }))} />
              </>
            )}
            {newTenant.dbType === "hosted" && (
              <input className="field-input" placeholder="Schema name (e.g. acme_corp)" value={newTenant.dbSchema} onChange={e => setNewTenant(p => ({ ...p, dbSchema: e.target.value }))} />
            )}
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            <button className="btn-primary" style={{ flex: 1 }} onClick={addTenant}>Create Client</button>
            <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setShowAddTenant(false)}>Cancel</button>
          </div>
        </div>
      </div>
    )}

  </div>
)}
        {/* ── Settings ── */}
        {nav === "settings" && (
          <div style={{ flex: 1, overflow: "auto", padding: "32px" }}>
            <div className="fu" style={{ marginBottom: 28 }}>
              <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Settings</h1>
              <p style={{ fontSize: 13, color: T.textSecondary }}>Manage your account preferences</p>
            </div>
            <div className="card fu1" style={{ maxWidth: 520, marginBottom: 16 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Account</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {[["Display name", user.name], ["Email", user.email], ["Tenant", tenantName]].map(([k, v]) => (
                  <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${T.border}` }}>
                    <span style={{ fontSize: 13, color: T.textSecondary }}>{k}</span>
                    <span style={{ fontSize: 13, color: T.textPrimary }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="card fu2" style={{ maxWidth: 520 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Session</h3>
              <button className="btn-secondary" style={{ color: T.error, borderColor: "rgba(248,81,73,.3)" }} onClick={onLogout}>Sign out</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── App ────────────────────────────────────────────────────────────────────────
export default function App() {
  const [screen, setScreen] = useState("login");
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);

  // On mount, check if user is already signed in (MSAL session)
// On mount, check if user is already signed in (MSAL session)
useEffect(() => {
  async function checkSession() {
    const msal = getMsalInstance();
    if (!msal) return;
    await msal.initialize();
    
    // Handle redirect coming back from Microsoft
    const redirectResult = await msal.handleRedirectPromise();
    if (redirectResult) {
      setUser({
        email: redirectResult.account.username,
        name: redirectResult.account.name || redirectResult.account.username.split("@")[0],
      });
      setToken(redirectResult.accessToken);
      sessionStorage.setItem("pvbi_token", redirectResult.accessToken);
      setScreen("dashboard");
      return;
    }

    // Check if already signed in
    const accounts = msal.getAllAccounts();
    if (accounts.length) {
      try {
        const result = await msal.acquireTokenSilent({
          scopes: ["openid", "profile", "email"],
          account: accounts[0],
        });
        setUser({ email: accounts[0].username, name: accounts[0].name || accounts[0].username.split("@")[0] });
        setToken(result.accessToken);
        sessionStorage.setItem("pvbi_token", result.accessToken);
        setScreen("dashboard");
      } catch {
        // Silent token failed, stay on login
      }
    }
  }
  checkSession();
}, []);

 const handleLogin = (u, t) => {
    setUser(u); 
    setToken(t); 
    sessionStorage.setItem("pvbi_token", t);
    setScreen("dashboard");
  };

  const handleLogout = async () => {
    try { await signOut(); } catch {}
    setUser(null); setToken(null); setScreen("login");
  };

  return (
    <ErrorBoundary>
      <>
        <style>{css}</style>
        <div className="grid-bg" />
        <div className="orb" style={{ width: 500, height: 500, top: -160, left: -120, background: "rgba(196,122,16,0.07)" }} />
        <div className="orb" style={{ width: 350, height: 350, bottom: -100, right: -80, background: "rgba(196,122,16,0.04)" }} />
        {screen === "login" && <Login onLogin={handleLogin} />}
        {screen === "dashboard" && <Dashboard user={user} token={token} onLogout={handleLogout} />}
      </>
    </ErrorBoundary>
  );
}
